import { Formatters } from '../../shared/lib/formatters';
import {
  LADDER, BOOKING_KEYS, BOOKING_LABEL, BOOKING_DEFAULT_COLS, FORMULA_BOUNDS, RATE_CHANNELS, RATE_CHIPS, PRESETS_FALLBACK, PRESET_IDS,
  normColumns, formulaColumns, isLegacy, presetOf, presetBesideRates, applyPreset, sameRates, presetName, statusOf, isStatus, fmtMoney, fmtDelta, fmtStatus,
  FROM_LABEL, DEFAULT_THRESHOLDS, monthLabel, monthFromNow, EXPLAIN, scheduleText,
} from '../../shared/lib/statementLadder';
import type { Columns, Preset, Status, Thresholds, BookingKey } from '../../shared/lib/statementLadder';

// ── R119 statement formula builder ───────────────────────────────────────────
// (Jesse: "I feel like this should be a lot closer. The gross formula builder
// is lacking I think.") The R118 gross modal could only say what "counts toward
// gross"; the statements differ in five places it had no control for (cleaning
// paid to the owner as Non-Commissionable Fees, the channel-fee base, the
// commission base, money kept from cancelled bookings, expenses). This overlay
// IS the VR Platform ladder: LEFT, one row per statement column in statement
// order with that column's definition; RIGHT, the formula run on one property's
// month next to its statement (POST /api/owner-finance/preview with the DRAFT —
// nothing is saved until Save). Opened from Money → Formulas & Fees (New / Edit
// / Preview / Duplicate) and from Property Settings' "Preview · Learn" links
// (one-shot sessionStorage 'sf_focus'). Admin + director only: the page that
// mounts it hides it on a 403.
// R121 (Jesse, Seagirt's August: "how some still aren't adding up when others
// are… the gross, the channel fees, and the net before expenses should be pretty
// much perfect"): the preview asks for diagnose — every off / close cell says WHY
// (a cause chip) and, where a setting would fix it, a Fix button whose patch was
// tested on every statement month on file (lib/ownerDiagnose). A Fix changes the
// DRAFT only: columns into the formula, facts (the company fee by booking date,
// the Vrbo fee basis…) into the draft facts, which "Save facts" writes to the
// property. Learn is a primary action. No cleaning estimate anywhere: Expenses
// and Net Income come from the statement (no Δ); without one the preview stops at
// Net before expenses, lists what Dash logged and says "after logged items".

const esc = (v: unknown): string => Formatters.escapeHtml(String(v == null ? '' : v));
const BUILTIN = 'statement_default';
const DEBOUNCE_MS = 300;
const r2 = (n: number) => Math.round(n * 100) / 100;
const num = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
// R121 review #11: up to 3 decimals (VR Platform's Vrbo fee is 7.335% of the guest total: the banner says the fact as it is saved)
const pctTxt = (v: unknown): string => { const n = num(v); return n == null ? '—' : `${Math.round(n * 1000) / 1000}%`; };
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
// R121 review #11/#13/#21: the channel rates of a profile, one per channel (null = not set), as the
// server's pickRates (lib/ownerDiagnose) and POST /profile see them: a profile's channel_rates is
// stored and sent WHOLE (the preview lays a draft's channel_rates over the stored one as one object)
const pickRates = (r: unknown): Record<string, number | null> => Object.fromEntries(RATE_CHANNELS.map(([k]) => [k, r && typeof r === 'object' ? num((r as any)[k]) : null]));
const sameVal = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** What the page that mounts the builder (SettingsPage) lends it. */
export interface BuilderHost {
  store(): any;                                  // GET /api/owner-finance/rules (rules, presets, assign, listings, profiles, channel_rates, default_rule)
  calib(): any | null;                           // the last "Match the statements" answer — "n properties look like this"
  adoptStore(j: any): void;                      // a write's { version, store } → the page adopts it and redraws its table
  openAssign(formulaId: string): void;           // the page's assign modal (stacks above the builder)
  editFacts(listingId: string): void;            // the page's property-facts modal (stacks above the builder)
  factsSaved?(listingId: string): void;          // R121 review #24: the builder wrote this property's facts (Save facts, Learn's Assign) — a pending "Match the statements" row re-checks
  saved(formula: any, isNew: boolean, name: string): void;   // after a Save: the page closes the builder, notes it, opens Assign for a new one
  closed(): void;
}
export interface BuilderOpen {
  formula?: any | null;          // a stored formula (edit / preview), or null = new
  copy?: boolean;                // Duplicate: a NEW formula drafted from `formula`
  mode?: 'edit' | 'preview';     // preview = opens on the Preview tab (≤ 900 px)
  listingId?: string;            // the property the preview runs on (default: the first one using the formula)
  month?: string;                // 'YYYY-MM' (default: the latest month with a statement)
  learn?: boolean;               // run Learn on open (Property Settings' "Learn" link)
}
interface Draft { id: string | null; name: string; description: string; columns: Columns }

// the four cleaning cards (+ the pre-R119 'never' value, shown only while a legacy formula still has it)
const CLEANING_CARDS: Array<[Columns['cleaning'], string, string]> = [
  ['gross', 'In Gross', 'the Cleaning Fees column — commission is charged on it'],
  ['noncomm', 'Paid to the owner as Non-Commissionable Fees', 'the owner gets it; no commission'],
  ['company', 'Kept by the company', 'not on the owner\'s statement'],
  ['auto', 'Automatic', 'from each property\'s commission formula'],
];
const CLEAN_WORDS: Record<string, string> = { gross: 'cleaning in Gross', noncomm: 'cleaning paid to owner (non-comm)', company: 'cleaning kept by the company', auto: 'cleaning automatic', auto_not_gross: 'cleaning automatic, never Gross' };
const OWNER_FEE_WORDS: Record<string, string> = { resolutions: 'owner fees in Resolutions', noncomm: 'owner fees paid to owner', company: 'owner fees kept by the company' };
const SOURCE_WORDS: Record<string, string> = { published: 'published statement', batch: 'uploaded statement (not published yet)' };
// R121: a cause in a few words (the chip under an off / close cell); its sentence is the chip's title
const CAUSE_SHORT: Record<string, string> = {
  fee_on_cleaning: 'fee on cleaning too', fee_off_cleaning: 'fee on rents only', fee_on_owner_fees: 'fee on owner fees', cleaning_in_gross: 'cleaning in Gross',
  cleaning_noncomm: 'cleaning paid to owner', cleaning_company: 'no cleaning on statement', cleaning_amount: 'cleaning differs (data)', company_fee_level: 'company fee when booked',
  company_fee_value: 'company fee', payout_differs: 'payout differs (data)', unitemized_fee: 'pet or extra fee (data)', rents_higher: 'payout change (data)', vrbo_rents: 'Vrbo change (data)',
  total_differs: 'tax in total (data)', owner_fee: 'owner fee in Resolutions', refund: 'refund (data)', resolutions_other: 'statement charge (data)', booked_fee: 'no channel rate set',
  follows_rents: 'follows the rents', vrbo_total: 'Vrbo fee on guest total', vrbo_rate: 'Vrbo % differs (data)', channel_rate: 'channel rate', fee_other: 'channel fee (data)',
  commission_net: 'commission on net', commission_gross: 'commission on gross', commission_section: 'commission section', commission_rate: 'commission rate', follows_base: 'follows its base', commission_other: 'commission (data)',
  long_stay: 'long stay (data)', cancellation_kept: 'cancellation (data)', adjustment_row: 'adjustment row', cancelled_row: 'cancellation not counted', unmatched_row: 'no booking matches',
  formula_only_row: 'not on the statement', unexplained: 'no single cause',
};

export class StatementBuilder {
  private el: HTMLElement | null = null;
  private orig: any = null;                 // the stored formula being edited (null = new)
  private draft!: Draft;
  private snap = '';                        // the draft as opened — "dirty" = differs
  private tab: 'ladder' | 'preview' = 'ladder';
  private raw: Record<string, string> = {}; // numeric inputs as typed (a bad value stays on screen)
  private bad: Record<string, string> = {}; // field → its error
  private rateMode: 'property' | 'fixed' = 'property';
  private listingId = '';
  private month = '';
  private search = '';
  private statementMonths = new Set<string>();   // per LISTING: cleared when the property changes
  private monthPicked = false;   // the office (or sf_focus) chose the month — else it follows the newest statement
  // ranProfile (R121 review #11): the draft facts the reply on screen ran with — its Fix patches are against them
  private pv: { seq: number; ctrl: AbortController | null; timer: any; data: any; err: string; pending: boolean; ranProfile: Record<string, unknown> | null } = { seq: 0, ctrl: null, timer: null, data: null, err: '', pending: false, ranProfile: null };
  private learn: { busy: boolean; data: any; err: string; applied: string; applying: boolean } = { busy: false, data: null, err: '', applied: '', applying: false };
  // R121 review #13/#21: ONE draft-facts model. learnedProfile = every fact the preview runs over the
  // stored ones (Learn's, with each Fix's laid on top); fixFacts = the part a Fix put there, always
  // also in learnedProfile with the same value (the banner and "Save facts" use it). Learn merges
  // UNDER the Fix facts, never replaces them; "Save facts" takes out of the draft only what it saved.
  private learnedProfile: Record<string, unknown> | null = null;   // the draft facts, sent as the preview's draft profile until saved
  private fixFacts: Record<string, unknown> | null = null;   // R121: facts a Fix put in the draft (not saved yet — "Save facts"); channel_rates = only the channels a Fix changed
  private fixNote = '';                                       // R121: what the last Fix did (said above the preview)
  private diffOnly = false;
  private cols = new Set<BookingKey>(BOOKING_DEFAULT_COLS);
  private busy = false;
  private errMsg = '';

  constructor(private root: HTMLElement, private host: BuilderHost) {}

  isOpen(): boolean { return !!(this.el && this.el.isConnected); }

  open(o: BuilderOpen = {}): void {
    if (this.isOpen()) this.teardown();
    const f = o.formula || null;
    this.orig = f && !o.copy ? f : null;
    const name = f ? (o.copy ? this.copyName(String(f.name || '')) : String(f.name || '')) : '';
    this.draft = { id: this.orig ? String(this.orig.id) : null, name, description: f && f.description ? String(f.description) : '', columns: f ? formulaColumns(f) : normColumns({ cleaning: 'gross', fee_on_cleaning: true, commission_base: 'net' }) };
    this.snap = JSON.stringify(this.draft);
    this.raw = {}; this.bad = {}; this.errMsg = ''; this.busy = false;
    this.rateMode = this.draft.columns.commission_rate != null ? 'fixed' : 'property';
    if (this.draft.columns.commission_rate != null) this.raw.commission_rate = String(this.draft.columns.commission_rate);
    if (this.draft.columns.min_fee != null) this.raw.min_fee = String(this.draft.columns.min_fee);
    for (const [k] of RATE_CHANNELS) { const v = this.draft.columns.channel_rates ? this.draft.columns.channel_rates[k] : null; if (v != null) this.raw[`rate_${k}`] = String(v); }
    this.tab = o.mode === 'preview' ? 'preview' : 'ladder';
    this.learn = { busy: false, data: null, err: '', applied: '', applying: false };
    this.learnedProfile = null; this.fixFacts = null; this.fixNote = ''; this.search = ''; this.diffOnly = false;
    // R119 review #13: the sequence carries on across a re-open (Learn's Assign re-opens the
    // builder on the same property + month) — a reply still in flight from before is stale
    this.pv = { seq: this.pv.seq + 1, ctrl: null, timer: null, data: null, err: '', pending: false, ranProfile: null };
    this.listingId = this.pickListing(o.listingId);
    this.statementMonths.clear();
    this.monthPicked = /^\d{4}-\d{2}$/.test(String(o.month || ''));
    this.month = this.monthPicked ? String(o.month) : this.defaultMonth();
    const el = document.createElement('div');
    el.className = 'sb-overlay'; el.id = 'sb-overlay';
    el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true'); el.setAttribute('aria-labelledby', 'sb-title');
    this.el = el;
    this.root.appendChild(el);
    this.render();
    this.bind();
    if (this.tab === 'ladder') this.el.querySelector<HTMLInputElement>('#sb-name')?.focus();
    this.schedulePreview(true);
    if (o.learn && this.listingId) void this.runLearn();
  }

  /** Close (asks first when there are unsaved changes, unless forced). → closed? */
  close(force = false): boolean {
    if (!this.isOpen()) return true;
    if (!force) {
      // R121 review #20: facts a Fix (or Learn) put in the draft are unsaved work too — ✕, Cancel and
      // Escape used to drop them without a word (dirty() only compares the formula)
      const facts = this.unsavedFacts(); const dirty = this.dirty();
      const where = this.listingName(this.listingId);
      const msg = dirty && facts ? `Discard the changes to this statement formula, and the property facts not saved to ${where} yet (${facts})?`
        : facts ? `Discard the property facts not saved to ${where} yet (${facts})?`
        : dirty ? 'Discard the changes to this statement formula?' : '';
      if (msg && !confirm(msg)) return false;
    }
    this.teardown();
    this.host.closed();
    return true;
  }
  private teardown(): void {
    clearTimeout(this.pv.timer);
    try { this.pv.ctrl?.abort(); } catch { /* gone */ }
    this.pv.seq++;   // any reply still in flight is now stale
    this.el?.remove(); this.el = null;
  }
  /** The property's facts or the store changed (facts modal, assign): re-run the preview. */
  refresh(): void { if (this.isOpen()) { this.drawFooter(); this.drawPreviewBar(); this.schedulePreview(true); } }
  /** The store moved (an assign elsewhere): counts + property list follow. */
  storeChanged(): void { if (this.isOpen()) { this.drawFooter(); this.drawPreviewBar(); } }

  // ── model helpers ─────────────────────────────────────────────────────────
  private store(): any { return this.host.store() || {}; }
  private presets(): Preset[] {
    const p = this.store().presets;
    return Array.isArray(p) && p.length ? p.filter((x: any) => x && (PRESET_IDS as readonly string[]).includes(String(x.id))) : [...PRESETS_FALLBACK];
  }
  private dirty(): boolean { return JSON.stringify(this.draft) !== this.snap; }
  // R121 review #20: the draft facts nothing has saved yet, in words ('' = none): a Fix's (each one
  // named), and Learn's until its Assign saves them
  private unsavedFacts(): string {
    const ff = this.fixFacts && Object.keys(this.fixFacts).length ? this.fixFacts : null;
    const words = ff ? this.factWords(ff) : [];
    if (ff && !words.length) words.push('the facts a Fix set');
    if (this.learnedOnly()) words.push('the facts Learn found');
    return words.join(' · ');
  }
  // Learn's facts are in the draft (keys no Fix set), not applied yet
  private learnedOnly(): boolean {
    const lp = this.learnedProfile;
    return !!lp && !this.learn.applied && Object.keys(lp).some(k => !(this.fixFacts && k in this.fixFacts));
  }
  // facts in words (the banner, the prompts) — every channel rate a Fix set, the Vrbo one with its basis
  private factWords(f: any): string[] {
    const words: string[] = [];
    if (Array.isArray(f.company_fee_schedule) && f.company_fee_schedule.length > 1) words.push(`company fee ${scheduleText(f.company_fee_schedule)}`);
    else if (num(f.company_fee) != null) words.push(`company fee ${fmtMoney(f.company_fee)} on every booking`);
    const cr = f.channel_rates && typeof f.channel_rates === 'object' ? f.channel_rates : {};
    for (const [k, l] of RATE_CHANNELS) {
      if (k === 'vrbo' && f.vrbo_fee_basis === 'guest_total') words.push(`Vrbo fee ${pctTxt(cr.vrbo)} of the guest total`);
      else if (num(cr[k]) != null) words.push(`${l} fee ${pctTxt(cr[k])}`);
    }
    if (num(f.commission_rate) != null) words.push(`commission ${pctTxt(f.commission_rate)}`);
    if (num(f.owner_fee_per_booking) != null) words.push(`owner fee ${fmtMoney(f.owner_fee_per_booking)} per booking`);
    return words;
  }
  // the property's stored facts (the host's store)
  private storedProfile(): any { const p = (this.store().profiles || {})[this.listingId]; return p && typeof p === 'object' ? p : null; }
  // the draft facts with the Fix facts laid on top (channel_rates: the whole set, the Fix's channels over
  // `base`'s, else over the stored ones — the preview sends a profile's channel_rates as one object)
  private withFixFacts(base: Record<string, unknown> | null): Record<string, unknown> | null {
    const out: Record<string, any> = { ...(base || {}) };
    for (const [k, v] of Object.entries(this.fixFacts || {})) {
      if (k === 'channel_rates' && v && typeof v === 'object') out.channel_rates = { ...pickRates(out.channel_rates || (this.storedProfile() || {}).channel_rates), ...(v as object) };
      else out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  }
  private builtin(): boolean { return !!this.orig && String(this.orig.id) === BUILTIN; }
  private copyName(n: string): string { return `${n} (copy)`.slice(0, FORMULA_BOUNDS.name_max); }
  private listings(): any[] {
    const ls = Array.isArray(this.store().listings) ? this.store().listings : [];
    return [...ls].sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')));
  }
  private listingName(id: string): string { const l = this.listings().find((x: any) => String(x.id) === String(id)); return l ? String(l.name || id) : String(id); }
  private resolvesTo(lid: string): string { const s = this.store(); return (s.assign && s.assign[lid]) || s.default_rule || BUILTIN; }
  private counts(): { all: number; explicit: number } {
    const id = this.draft.id; let all = 0, explicit = 0;
    if (!id) return { all, explicit };
    const s = this.store();
    for (const l of this.listings()) { const a = s.assign ? s.assign[String(l.id)] : undefined; if ((a || s.default_rule) === id) all++; if (a === id) explicit++; }
    return { all, explicit };
  }
  private pickListing(want?: string): string {
    const ls = this.listings();
    if (want && ls.some((l: any) => String(l.id) === String(want))) return String(want);
    if (want) return String(want);   // not in the active list (excluded?) — still preview it
    const id = this.draft.id; const s = this.store();
    const explicit = id ? ls.find((l: any) => s.assign && s.assign[String(l.id)] === id) : null;
    if (explicit) return String(explicit.id);
    const viaDefault = id ? ls.find((l: any) => this.resolvesTo(String(l.id)) === id) : null;
    return viaDefault ? String(viaDefault.id) : ls.length ? String(ls[0].id) : '';
  }
  // the latest month known to have a statement, else last month (statements publish for the month before)
  private defaultMonth(): string {
    const known = [...this.statementMonths].sort();
    return known.length ? known[known.length - 1] : monthFromNow(-1);
  }
  private draftFormula(): any {
    const d = this.draft;
    const out: any = { name: d.name.trim(), description: d.description.trim(), schema: 2, preset: presetOf(d.columns, this.presets()), columns: { ...d.columns } };
    if (d.id) out.id = d.id;
    return out;
  }
  private profileWord(src: string): string {
    if (src !== 'profile') return FROM_LABEL[src] || src;
    const p = (this.store().profiles || {})[this.listingId];
    return this.learnedProfile ? 'learned (not saved yet)' : p && p.source === 'learned' ? 'learned' : 'property facts';
  }

  // ── render ────────────────────────────────────────────────────────────────
  private render(): void {
    if (!this.el) return;
    this.el.dataset.tab = this.tab;
    const legacy = this.orig && isLegacy(this.orig);
    this.el.innerHTML = `
      <style>${STYLE}</style>
      <div class="sb-panel">
        <div class="sb-hdr">
          <div class="sb-hdr-main">
            <div class="sb-hdr-top">
              <h3 id="sb-title">${this.orig ? 'Statement formula' : 'New statement formula'}</h3>
              <span class="sb-dim" id="sb-preset-name"></span>
            </div>
            <div class="sb-names">
              <input id="sb-name" class="sb-in sb-name" maxlength="${FORMULA_BOUNDS.name_max}" value="${esc(this.draft.name)}" placeholder="Name, e.g. Cleaning to owner · 20% of net" aria-label="Formula name">
              <input id="sb-desc" class="sb-in sb-desc" maxlength="${FORMULA_BOUNDS.description_max}" value="${esc(this.draft.description)}" placeholder="Description (optional)" aria-label="Description">
            </div>
            <div class="sb-presets" id="sb-presets" role="group" aria-label="Presets"></div>
            <div class="sb-explain" id="sb-explain"><span><b>Gross Revenue is built here.</b> It is one column of this formula: Rents + Resolutions/Fees + Cleaning Fees (when cleaning is In Gross).</span>
              <details class="sb-explain-d"><summary>Statement formula vs Commission (P&amp;L)</summary><p>${esc(EXPLAIN.statement)}</p><p>${esc(EXPLAIN.commission)}</p><p>${esc(EXPLAIN.automatic)}</p></details></div>
          </div>
          <button class="cfg-close" data-sb="close" aria-label="Close">✕</button>
        </div>
        ${legacy ? `<div class="sb-banner sb-banner-warn" id="sb-legacy" role="note">Made before R119 — cleaning follows each property's commission formula; saving converts it to a statement formula.</div>` : ''}
        ${this.builtin() ? `<div class="sb-banner" id="sb-builtin" role="note">This is the built-in default — it can't be changed. <strong>Save as new</strong> makes a copy you can edit and assign.</div>` : ''}
        <div class="sb-tabs" role="tablist">
          <button role="tab" data-sb-tab="ladder" aria-selected="${this.tab === 'ladder'}">Ladder</button>
          <button role="tab" data-sb-tab="preview" aria-selected="${this.tab === 'preview'}">Preview</button>
        </div>
        <div class="sb-body">
          <section class="sb-left" id="sb-left" aria-label="Statement ladder"></section>
          <section class="sb-right" id="sb-prev" aria-label="Preview">
            <div id="sb-p-bar"></div>
            <div id="sb-learn-banner"></div>
            <div id="sb-p-out"></div>
          </section>
        </div>
        <div class="sb-foot" id="sb-foot"></div>
      </div>`;
    this.drawPresets(); this.drawLeft(); this.drawPreviewBar(); this.drawLearnBanner(); this.drawOut(); this.drawFooter();
  }

  private drawPresets(): void {
    const box = this.el?.querySelector<HTMLElement>('#sb-presets');
    if (!box) return;
    const cur = presetOf(this.draft.columns, this.presets());
    const looks = this.looksLike();
    const chip = (id: string, label: string) => {
      const n = looks[id] || 0;
      const tip = `${label}${n ? ` — ${plural(n, 'property looks', 'properties look')} like this (last Match)` : ''}`;
      return `<button type="button" class="sb-chip${cur === id ? ' on' : ''}" data-sb-preset="${esc(id)}" title="${esc(tip)}" aria-pressed="${cur === id}" ${id === 'custom' ? 'tabindex="-1"' : ''}>${esc(presetName(id))}${n ? `<sup>${n}</sup>` : ''}</button>`;
    };
    box.innerHTML = this.presets().map(p => chip(String(p.id), String(p.label || p.id))).join('') + chip('custom', 'Custom — any other shape');
    const pn = this.el?.querySelector<HTMLElement>('#sb-preset-name');
    const p = this.presets().find(x => x.id === cur);
    // R119 review #15: channel rates are part of the shape (the server's presetOf) — "D" with
    // rates of its own is stored as Custom, and the header says which preset it started from
    const near = p ? null : presetBesideRates(this.draft.columns, this.presets());
    if (pn) pn.textContent = p ? `${presetName(cur)} · ${p.label}` : near ? `Custom shape — ${presetName(near)} with its own channel rates` : 'Custom shape';
  }
  // the last Match's suggested preset per property → { preset: n }
  private looksLike(): Record<string, number> {
    const c = this.host.calib(); const out: Record<string, number> = {};
    for (const l of ((c && Array.isArray(c.listings)) ? c.listings : [])) { const p = l && l.suggested_formula && l.suggested_formula.preset; if (p) out[p] = (out[p] || 0) + 1; }
    return out;
  }

  // LEFT: the 11 ladder rows, statement order
  private drawLeft(): void {
    const box = this.el?.querySelector<HTMLElement>('#sb-left');
    if (!box) return;
    const c = this.draft.columns;
    box.innerHTML = `<div class="sb-pane-h"><h4>Statement ladder</h4><span class="sb-dim">VR Platform columns, in statement order</span></div>` + LADDER.map((r, i) => `
      <div class="sb-row${r.computed ? ' sb-computed' : ''}${this.rowErr(r.key) ? ' sb-row-bad' : ''}" data-sb-row="${r.key}">
        <div class="sb-row-h"><span class="sb-n">${i + 1}</span><div><div class="sb-row-l">${esc(r.label)}</div><div class="sb-row-d">${esc(this.definition(r.key, c))}</div></div></div>
        <div class="sb-row-c">${this.control(r.key, c)}${this.rowErr(r.key) ? `<div class="sb-row-err" role="alert">${esc(this.rowErr(r.key))}</div>` : ''}</div>
      </div>`).join('');
  }
  private rowErr(key: string): string {
    const map: Record<string, string[]> = { channel_fee: RATE_CHANNELS.map(([k]) => `rate_${k}`), commission: ['commission_rate', 'min_fee'], cleaning_col: ['cleaning'] };
    return (map[key] || []).map(k => this.bad[k]).filter(Boolean)[0] || '';
  }
  private definition(key: string, c: Columns): string {
    switch (key) {
      case 'rents': return 'Statement rent per channel, read from each booking: less cleaning, taxes and the company fee in force when it was booked.';
      case 'resolutions': return 'Owner guest fees (pet, extra guest, resort) and refunds.';
      case 'cleaning_col': return 'The guest\'s cleaning fee, when it counts in Gross.';
      case 'channel_fee': return 'What Airbnb, Vrbo, Booking.com and card processing take.';
      case 'noncomm': {
        const parts: string[] = [];
        if (c.cleaning === 'noncomm') parts.push('cleaning paid to the owner');
        if (c.cleaning === 'auto' || c.cleaning === 'auto_not_gross') parts.push('cleaning, when the commission formula gives it to the owner with no commission');
        if (c.owner_fees === 'noncomm') parts.push('owner guest fees');
        return parts.length ? `Paid to the owner with no commission: ${parts.join(' + ')}.` : 'Nothing lands here with this formula.';
      }
      case 'commission': return 'Our management fee — never taken on Non-Commissionable Fees.';
      case 'expenses': return 'From the statement. Until it is out: only what Dash logged as billable to the owner.';
      case 'net_income': return '= Total − Management Commission − Expenses. Only a statement has it: without one the preview stops at Net before expenses.';
      default: { const r = LADDER.find(x => x.key === key); return r && r.computed ? `= ${r.computed}` : ''; }
    }
  }
  // R121: a fact's chip only when Learn found it (a schedule, the Vrbo fee on the guest total). R121 fix round 3:
  // or when Learn set bookings aside rather than guess (its title says what it saw and that it can be set by hand)
  private evIf(key: string): string {
    const e = this.learn.data && this.learn.data.evidence ? this.learn.data.evidence[key] : null;
    if (e && e.value) return this.ev(key);
    if (e && Array.isArray(e.declined) && e.declined.length) return ` <span class="sb-ev" data-sb-ev="${esc(key)}" title="${esc(e.detail || e.declined.join('; '))}">no fee change learned</span>`;
    return '';
  }
  private ev(key: string): string {
    const e = this.learn.data && this.learn.data.evidence ? this.learn.data.evidence[key] : null;
    if (!e || typeof e !== 'object') return '';
    const unit = e.unit || ({ cleaning: 'statements', commission: 'rows' } as Record<string, string>)[key] || 'rows';
    const n = num(e.n), of = num(e.of);
    if (of === 0) return '';   // nothing to learn from (e.g. no owner fees on the statements)
    const lead = key === 'commission' && (e.rate != null || e.base) ? `${e.rate != null ? pctTxt(e.rate) : ''}${e.base ? ` of ${e.base === 'net' ? 'net' : 'gross'}` : ''} · `
      : '';
    const body = n != null && of != null ? `${n}/${of} ${unit}` : n != null ? `${n} ${unit}` : 'learned';
    return ` <span class="sb-ev" data-sb-ev="${esc(key)}" title="${esc(e.detail || 'learned from this property\'s statements')}">${esc(lead + body)}</span>`;
  }
  private control(key: string, c: Columns): string {
    const sel = (on: boolean) => (on ? 'selected' : '');
    const chk = (on: boolean) => (on ? 'checked' : '');
    const s = this.store(); const shared = s.channel_rates || {};
    switch (key) {
      case 'rents':
        return `<label class="sb-check"><input type="checkbox" id="sb-c-cancellations" ${chk(c.cancellations === 'airbnb_retained')}> Count money Airbnb keeps on cancelled bookings</label>${this.ev('cancellations')}${this.evIf('company_fee_schedule')}`;
      case 'resolutions':
        return `<label class="sb-lbl" for="sb-c-owner_fees">Owner guest fees (pet, extra guest, resort)</label>
          <select class="sb-in" id="sb-c-owner_fees"><option value="resolutions" ${sel(c.owner_fees === 'resolutions')}>in Resolutions (part of Gross)</option><option value="noncomm" ${sel(c.owner_fees === 'noncomm')}>paid to owner, Non-Commissionable</option><option value="company" ${sel(c.owner_fees === 'company')}>kept by the company</option></select>`;
      case 'cleaning_col': {
        const cards = [...CLEANING_CARDS];
        if (c.cleaning === 'auto_not_gross') cards.push(['auto_not_gross', 'Automatic, never in Gross (made before R119)', 'pick one of the four to save']);
        return `<div class="sb-cards" role="radiogroup" aria-label="Cleaning">${cards.map(([v, t, d]) => `<label class="sb-card${c.cleaning === v ? ' on' : ''}"><input type="radio" name="sb-cleaning" value="${v}" ${chk(c.cleaning === v)}><span><b>${esc(t)}</b><span>${esc(d)}</span></span></label>`).join('')}</div>${this.ev('cleaning')}`;
      }
      case 'channel_fee':
        return `<div class="sb-lbl">Charged on</div>
          <div class="sb-on-row">
            <label class="sb-check sb-locked" title="Rents always carry the channel fee"><input type="checkbox" checked disabled> Rents</label>
            <label class="sb-check">Cleaning <select class="sb-in sb-in-auto" id="sb-c-fee_on_cleaning" aria-label="Channel fee on cleaning"><option value="auto" ${sel(c.fee_on_cleaning === 'auto')}>Automatic</option><option value="true" ${sel(c.fee_on_cleaning === true)}>On</option><option value="false" ${sel(c.fee_on_cleaning === false)}>Off</option></select></label>
            <label class="sb-check"><input type="checkbox" id="sb-c-fee_on_owner_fees" ${chk(c.fee_on_owner_fees)}> Owner fees</label>
          </div>${this.ev('fee_on_cleaning')}${this.ev('fee_on_owner_fees')}${this.evIf('vrbo_fee_basis')}${this.feeGuard(c)}
          <div class="sb-rates">${RATE_CHANNELS.map(([k, l]) => { const sh = num(shared[k]); return `<label><span>${esc(l)} %</span><input class="sb-in" type="number" inputmode="decimal" min="${FORMULA_BOUNDS.channel_rate[0]}" max="${FORMULA_BOUNDS.channel_rate[1]}" step="0.01" data-sb-rate="${k}" value="${esc(this.raw[`rate_${k}`] ?? '')}" placeholder="${sh != null ? `${sh} shared` : 'booked fee'}"></label>`; }).join('')}</div>
          <div class="sb-hint">Blank = the property's own rate, else the shared rate.</div>`;
      case 'commission': {
        const base = (v: string, l: string) => `<label class="sb-seg-b${c.commission_base === v ? ' on' : ''}"><input type="radio" name="sb-base" value="${v}" ${chk(c.commission_base === v)}>${esc(l)}</label>`;
        const fixed = this.rateMode === 'fixed';
        return `<div class="sb-lbl">Base</div>
          <div class="sb-seg" role="radiogroup" aria-label="Commission base">${base('gross', '% of Gross')}${base('net', '% of Net Revenue')}${base('auto', 'Automatic')}</div>
          <div class="sb-lbl">Rate</div>
          <label class="sb-check"><input type="radio" name="sb-ratemode" value="property" ${chk(!fixed)}> each property's rate</label>
          <label class="sb-check"><input type="radio" name="sb-ratemode" value="fixed" ${chk(fixed)}> fixed</label>
          <div class="sb-ratechips${fixed ? '' : ' sb-dimmed'}">${RATE_CHIPS.map(p => `<button type="button" class="sb-chip sb-chip-s${fixed && num(this.raw.commission_rate) === p ? ' on' : ''}" data-sb-ratechip="${p}">${p}</button>`).join('')}
            <input class="sb-in sb-in-s" type="number" inputmode="decimal" id="sb-c-commission_rate" min="${FORMULA_BOUNDS.commission_rate[0]}" max="${FORMULA_BOUNDS.commission_rate[1]}" step="0.1" value="${esc(this.raw.commission_rate ?? '')}" placeholder="%" ${fixed ? '' : 'disabled'} aria-label="Fixed commission %"></div>
          <div class="sb-hint" id="sb-rate-hint">${fixed ? 'A rate saved in a property\'s facts still wins over this fixed rate.' : 'The property\'s facts rate, else its commission formula\'s.'}</div>
          <label class="sb-lbl" for="sb-c-min_fee">Minimum per booking ($)</label>
          <input class="sb-in sb-in-m" type="number" inputmode="decimal" id="sb-c-min_fee" min="${FORMULA_BOUNDS.min_fee[0]}" max="${FORMULA_BOUNDS.min_fee[1]}" step="0.01" value="${esc(this.raw.min_fee ?? '')}" placeholder="the commission formula's">${this.ev('commission')}`;
      }
      case 'expenses':
        // R121 (Jesse: "let's not take guesses on expenses"): nothing to set — no cleaning estimate
        return `<div class="sb-hint" id="sb-exp-def">${esc(EXPLAIN.no_guess)}</div>`;
      default:
        return '';   // computed rows carry their definition only
    }
  }

  // R121 (skeptic: "Cleaning In Gross" with the fee on cleaning "Off" matches 4 of 381 in-Gross Airbnb
  // statement rows portfolio-wide; the other 377 charge 15.5% on rents + cleaning — Jesse's Seagirt draft
  // inherited "Off"): a warning with a one-click "charge it on cleaning too" — never a silent flip
  private feeGuard(c: Columns): string {
    if (!(c.cleaning === 'gross' && c.fee_on_cleaning === false)) return '';
    return `<div class="sb-guard" id="sb-guard-foc" role="note">Cleaning is In Gross but the channel fee skips it. Nearly every statement that puts cleaning in Gross charges the fee on rents + cleaning (377 of 381 Airbnb rows). <button type="button" class="btn btn-secondary btn-sm" data-sb="guard-foc">Charge it on cleaning too</button></div>`;
  }

  // RIGHT: property / month / Learn bar
  private drawPreviewBar(): void {
    const box = this.el?.querySelector<HTMLElement>('#sb-p-bar');
    if (!box) return;
    box.innerHTML = `
      <div class="sb-pane-h"><h4>Preview</h4><span class="sb-dim" id="sb-p-updating" aria-live="polite">${this.pv.pending ? 'Updating…' : ''}</span></div>
      <div class="sb-p-bar">
        <input class="sb-in sb-p-search" id="sb-p-search" type="search" placeholder="Find a property…" value="${esc(this.search)}" aria-label="Find a property">
        <select class="sb-in sb-p-listing" id="sb-p-listing" aria-label="Property">${this.listingOptions()}</select>
        <select class="sb-in sb-p-month" id="sb-p-month" aria-label="Month">${this.monthOptions()}</select>
      </div>
      <div class="sb-p-bar">
        <button class="btn btn-primary btn-sm" data-sb="learn" ${this.learn.busy || !this.listingId ? 'disabled' : ''} title="Reads every statement on file for this property and sets the formula and facts they follow">${this.learn.busy ? 'Learning…' : 'Learn from this property\'s statements'}</button>
        <label class="sb-check"><input type="checkbox" id="sb-p-diff" ${this.diffOnly ? 'checked' : ''}> Differences only</label>
        <button class="btn btn-ghost btn-sm" data-sb="facts" ${this.listingId ? '' : 'disabled'}>Edit property facts</button>
      </div>`;
  }
  private listingOptions(): string {
    const q = this.search.trim().toLowerCase();
    const id = this.draft.id;
    const all = this.listings();
    const shown = all.filter((l: any) => !q || String(l.name || '').toLowerCase().includes(q) || String(l.id) === this.listingId);
    const opt = (l: any) => `<option value="${esc(l.id)}" ${String(l.id) === this.listingId ? 'selected' : ''}>${esc(l.name || l.id)}</option>`;
    if (!all.some((l: any) => String(l.id) === this.listingId) && this.listingId) shown.unshift({ id: this.listingId, name: this.listingId });
    if (!shown.length) return '<option value="">No properties</option>';
    if (!id) return shown.map(opt).join('');
    const using = shown.filter((l: any) => this.resolvesTo(String(l.id)) === id);
    const other = shown.filter((l: any) => this.resolvesTo(String(l.id)) !== id);
    return (using.length ? `<optgroup label="Using this formula">${using.map(opt).join('')}</optgroup>` : '') + (other.length ? `<optgroup label="Other properties">${other.map(opt).join('')}</optgroup>` : '');
  }
  private monthOptions(): string {
    const out: string[] = [];
    for (let i = 2; i >= -18; i--) out.push(monthFromNow(i));
    if (this.month && !out.includes(this.month)) out.push(this.month);
    return out.map(m => `<option value="${m}" ${m === this.month ? 'selected' : ''}>${esc(monthLabel(m))}${this.statementMonths.has(m) ? ' ✓' : ''}</option>`).join('');
  }

  private drawUpdating(): void {
    const el = this.el?.querySelector<HTMLElement>('#sb-p-updating');
    if (el) el.textContent = this.pv.pending ? 'Updating…' : '';
    // R121 review #11: the Fix buttons on screen belong to the reply on screen — while the draft is
    // re-tested (a Fix just applied, any other change) they wait for the new reply's
    this.el?.querySelectorAll<HTMLButtonElement>('button[data-sb-fix]').forEach(b => { b.disabled = this.pv.pending; });
  }
  private fixLock(): string { return this.pv.pending ? ' disabled' : ''; }

  // RIGHT: Learn result banner
  private drawLearnBanner(): void {
    const box = this.el?.querySelector<HTMLElement>('#sb-learn-banner');
    if (!box) return;
    const L = this.learn;
    if (L.applied) { box.innerHTML = `<div class="sb-banner sb-banner-ok" role="status">${esc(L.applied)}</div>`; return; }
    if (L.busy) { box.innerHTML = `<div class="sb-banner" role="status">Reading this property's statements…</div>`; return; }
    if (L.err) { box.innerHTML = `<div class="sb-banner sb-banner-bad" role="alert">${esc(L.err)}</div>`; return; }
    const d = L.data;
    if (!d) { box.innerHTML = ''; return; }
    const m = d.match || {};
    const months: string[] = Array.isArray(d.months) ? d.months : [];
    const facts = d.profile_facts || {};
    const fl: string[] = [];
    if (num(facts.company_fee) != null) fl.push(`company fee ${fmtMoney(facts.company_fee)}`);
    if (num(facts.tot_pct) != null) fl.push(`Airbnb tax ${pctTxt(facts.tot_pct)}`);
    if (num(facts.tot_pct_direct) != null) fl.push(`direct tax ${pctTxt(facts.tot_pct_direct)}`);
    if (num(facts.tot_pct_bookingcom) != null) fl.push(`Booking.com tax ${pctTxt(facts.tot_pct_bookingcom)}`);
    if (num(facts.commission_rate) != null) fl.push(`commission ${pctTxt(facts.commission_rate)}`);
    // R121: the company fee by booking date, the Vrbo fee on the guest total, a flat owner fee (R119's cleaning cost is gone)
    if (Array.isArray(facts.company_fee_schedule) && facts.company_fee_schedule.length > 1) fl.push(`company fee ${scheduleText(facts.company_fee_schedule)}`);
    if (facts.vrbo_fee_basis === 'guest_total') fl.push(`Vrbo fee ${pctTxt(facts.channel_rates && facts.channel_rates.vrbo)} of the guest total`);
    if (num(facts.owner_fee_per_booking) != null) fl.push(`owner fee ${fmtMoney(facts.owner_fee_per_booking)} per booking`);
    const ladder = this.ladderBeforeAfter(d.ladder_before, d.ladder_after);
    const exact = this.exactBeforeAfter(d.ladder_before, d.ladder_after);
    const prop = this.listingName(this.listingId);
    box.innerHTML = `<div class="sb-banner sb-banner-learn" id="sb-learned" role="status">
      <div>${m.formula_id ? `Matches “${esc(m.name || m.formula_id)}”` : 'No formula has this shape'} —
        <button class="btn btn-primary btn-sm" data-sb="learn-apply" ${L.applying ? 'disabled' : ''}>${L.applying ? 'Saving…' : m.formula_id ? 'Assign' : 'Save as new'}</button>
        <span class="sb-dim">${m.formula_id ? `assigns it to ${esc(prop)}` : `creates it${m.name ? ` (“${esc(m.name)}”)` : ''} and assigns it to ${esc(prop)}`}, and saves its facts.</span></div>
      <div class="sb-dim">Learned from ${plural(months.length, 'statement month', 'statement months')}${months.length ? ` (${esc(monthLabel(months[0]))}${months.length > 1 ? ` – ${esc(monthLabel(months[months.length - 1]))}` : ''})` : ''}${fl.length ? ` · ${esc(fl.join(' · '))}` : ''}</div>
      ${exact ? `<div id="sb-learn-exact"><b>To the dollar</b> (gross, channel fees and net before expenses all within $1): ${exact}</div>` : ''}
      ${ladder ? `<div class="sb-dim">Statements within 1%: ${ladder}</div>` : ''}
    </div>`;
  }
  // R121 (skeptic: Learn said "2/3 statements match" while those cells were off by $23–$56): the
  // statements matching on gross, channel fees AND net before expenses within the preview's $1
  private exactBeforeAfter(b: any, a: any): string {
    const e = a && a.exact; if (!e || typeof e !== 'object') return '';
    const of = num(a.of);
    const be = b && b.exact ? num(b.exact.all) : null;
    return `${be != null ? `${be} → ` : ''}${num(e.all)}${of != null ? ` of ${plural(of, 'statement', 'statements')}` : ''}`;
  }
  // ladder_before / ladder_after: { gross, fee, commission, …, of } — "gross 1 → 2 of 2"
  private ladderBeforeAfter(b: any, a: any): string {
    if (!a || typeof a !== 'object') return '';
    const of = num(a.of ?? a.n ?? a.total);
    const words: Record<string, string> = { gross: 'gross', fee: 'channel fee', channel_fee: 'channel fee', commission: 'commission', total_less_commission: 'total − commission', owner_net: 'total − commission', all: 'all four' };
    const parts = Object.keys(a).filter(k => !['of', 'n', 'total', 'exact'].includes(k) && num(a[k]) != null)
      .map(k => `${esc(words[k] || k.replace(/_/g, ' '))} ${b && num(b[k]) != null ? `${num(b[k])} → ` : ''}${num(a[k])}${of != null ? ` of ${of}` : ''}`);
    return parts.join(' · ');
  }

  // RIGHT: the preview itself
  private drawOut(): void {
    const box = this.el?.querySelector<HTMLElement>('#sb-p-out');
    if (!box) return;
    const d = this.pv.data;
    if (!this.listingId) { box.innerHTML = '<div class="sb-empty">Pick a property to preview this formula on.</div>'; return; }
    if (this.pv.err && !d) { box.innerHTML = `<div class="sb-banner sb-banner-bad" role="alert">${esc(this.pv.err)} <button class="btn btn-secondary btn-sm" data-sb="retry">Try again</button></div>`; return; }
    if (!d) { box.innerHTML = '<div class="sb-empty">Running the formula on this month…</div>'; return; }
    const th: Thresholds = { ...DEFAULT_THRESHOLDS, ...(d.thresholds || {}) };
    const hasStmt = !!(d.totals && d.totals.statement);
    const src = d.statement_source ? (SOURCE_WORDS[d.statement_source] || d.statement_source) : '';
    const mLabel = monthLabel(d.month || this.month);
    box.innerHTML = `
      ${this.pv.err ? `<div class="sb-banner sb-banner-bad" role="alert">${esc(this.pv.err)} — showing the last result.</div>` : ''}
      <div class="sb-p-head"><strong>${esc((d.listing && d.listing.name) || this.listingName(this.listingId))}</strong> · ${esc(mLabel)}${src ? ` · <span class="sb-tag">${esc(src)}</span>` : ''}</div>
      ${hasStmt ? '' : `<div class="sb-banner" id="sb-forecast" role="note">No statement for ${esc(mLabel)} yet. The formula only: it stops at net before expenses, and expenses are what Dash logged.</div>`}
      ${this.fixHtml()}
      ${this.learnCtaHtml(d, hasStmt)}
      ${this.totalsHtml(d, th, hasStmt)}
      ${this.expensesHtml(d)}
      ${this.provHtml(d)}
      ${this.bookingsHtml(d, th, hasStmt)}`;
  }
  // R121: what a Fix did, and the facts it put in the draft ("Save facts" writes them to the property)
  private fixHtml(): string {
    const facts = this.fixFacts && Object.keys(this.fixFacts).length ? this.fixFacts : null;
    if (!this.fixNote && !facts) return '';
    // R121 review #11/#21: exactly the facts the draft runs with from a Fix (fixFacts is always in learnedProfile),
    // every channel a Fix set (a Vrbo fee on the guest total no longer hides the Airbnb rate beside it)
    const words: string[] = facts ? this.factWords(facts) : [];
    return `<div class="sb-banner sb-banner-ok" id="sb-fix-note" role="status">${this.fixNote ? `<div>${esc(this.fixNote)}</div>` : ''}${facts ? `<div>Property facts in this draft, not saved yet: ${esc(words.join(' · ') || 'changed')}. <button class="btn btn-primary btn-sm" data-sb="save-facts">Save facts to ${esc(this.listingName(this.listingId))}</button></div>` : ''}${!facts && this.dirty() ? '<div class="sb-dim">Save the formula to keep the change.</div>' : ''}</div>`;
  }
  // R121: "Learn" up front when the month doesn't match — with why, when the property is on Automatic
  private learnCtaHtml(d: any, hasStmt: boolean): string {
    if (!hasStmt || this.learn.data || this.learn.busy || this.learn.applied) return '';
    const st = (d.totals && d.totals.status) || {};
    const off = ['gross', 'channel_fee', 'owner_net'].filter(k => st[k] && st[k] !== 'ok');
    if (!off.length) return '';
    const from = (d.settings && d.settings.from) || {};
    const auto = d.formula && d.formula.preset === 'auto' && from.cleaning === 'revenue_formula';
    return `<div class="sb-banner sb-banner-learn" id="sb-learn-cta" role="note"><div><b>This month doesn't match its statement yet.</b> Learn reads every statement on file for this property and sets the formula and facts they follow; each Fix below was tested the same way.</div>${auto ? `<div class="sb-dim">${esc(EXPLAIN.automatic)}</div>` : ''}<div><button class="btn btn-primary btn-sm" data-sb="learn">Learn from this property's statements</button></div></div>`;
  }
  // R121: the causes of one cell — chips, with a Fix button where a tested setting fixes it
  private whyHtml(list: any[], max = 3): string {
    const ws = (list || []).filter(w => w && w.kind !== 'follows').slice(0, max);
    const follows = (list || []).filter(w => w && w.kind === 'follows');
    const items = (ws.length ? ws : follows.slice(0, 1)).map(w => {
      const label = CAUSE_SHORT[w.cause_code] || String(w.cause_code || '').replace(/_/g, ' ');
      const fx = w.fix && w.fix.id ? `<button type="button" class="sb-fix" data-sb-fix="${esc(w.fix.id)}" title="${esc(`${w.fix.label}. ${w.fix.gain_text || ''}`)}"${this.fixLock()}>Fix</button>` : '';
      return `<span class="sb-why sb-why-${esc(w.kind || 'data')}" data-sb-why="${esc(w.cause_code)}" title="${esc(w.text || '')}">${esc(label)}${w.amount != null && w.bookings ? ` · ${esc(plural(Number(w.bookings), 'booking', 'bookings'))}` : ''}${fx}</span>`;
    });
    return items.length ? `<div class="sb-whys">${items.join('')}</div>` : '';
  }
  private totalsHtml(d: any, th: Thresholds, hasStmt: boolean): string {
    const t = d.totals || {};
    const why: any[] = Array.isArray(t.why) ? t.why : [];
    const val = (o: any, k: string): number | null => {
      if (!o) return null;
      if (k === 'expenses') { const e = o.expenses; return e == null ? null : typeof e === 'number' ? e : num(e.total); }
      if (k === 'commission' && o.commission == null) return num(o.commission_total);
      if (k === 'owner_net') return num(o.net_before_expenses != null ? o.net_before_expenses : o.total_after_commission);
      return num(o[k]);
    };
    const row = (key: string, label: string, sub: boolean, opts: { noFormula?: boolean; noDelta?: boolean; noStmt?: boolean } = {}) => {
      const f = opts.noFormula ? null : val(t.formula, key), s = hasStmt && !opts.noStmt ? val(t.statement, key) : null;
      const dl = opts.noDelta || opts.noFormula ? null : t.diff && num(t.diff[key]) != null ? num(t.diff[key]) : f != null && s != null ? r2(f - s) : null;
      const st: Status | null = !hasStmt || dl == null ? null : isStatus(t.status && t.status[key]) ? t.status[key] : statusOf(dl, s || 0, th);
      const cause = st && st !== 'ok' ? this.whyHtml(why.filter(w => w.col === key)) : '';
      return `<tr data-sb-tot="${key}" class="${sub ? 'sb-sub' : ''}"><th scope="row">${esc(label)}</th><td class="num" data-label="Formula">${opts.noFormula ? '<span class="sb-dim" title="A formula doesn\'t compute this: the statement does">—</span>' : fmtMoney(f)}</td>${hasStmt ? `<td class="num" data-label="Statement">${fmtMoney(s)}</td><td class="num sb-d${st ? ` sb-${st}` : ''}" data-label="Δ" ${st ? `data-sb-status="${st}"` : ''}>${st ? esc(fmtStatus(st, dl)) : '—'}${cause}</td>` : ''}</tr>`;
    };
    const rows: string[] = [];
    for (const r of LADDER) {
      if (r.key === 'expenses' || r.key === 'net_income') continue;
      rows.push(row(r.key, r.label, !!r.computed));
      if (r.key === 'commission') rows.push(row('owner_net', 'Net before expenses', true));   // R121: Total − Management Commission — what Jesse checks
    }
    if (hasStmt) {
      // R121: a formula never computes these — the statement's figures, no Δ
      rows.push(row('expenses', 'Expenses', false, { noFormula: true }));
      rows.push(row('net_income', 'Net Income', true, { noFormula: true }));
    } else {
      const f = t.formula || {};
      // R121 review #5: Dash couldn't be read → what it logged is unknown, never "$0.00"
      const unread = !!(f.expenses && f.expenses.unavailable);
      const logged = f.expenses && !unread ? num(f.expenses.total) : null;
      rows.push(`<tr data-sb-tot="logged"><th scope="row">Logged in Dash</th><td class="num" data-label="Formula">${unread ? '<span class="sb-dim">unknown</span>' : fmtMoney(logged)}</td></tr>`);
      rows.push(`<tr data-sb-tot="after_logged" class="sb-sub"><th scope="row">After logged items <span class="sb-dim">(not a payout)</span></th><td class="num" data-label="Formula">${f.after_logged == null ? '<span class="sb-dim">unknown</span>' : fmtMoney(f.after_logged)}</td></tr>`);
    }
    return `<div class="sb-card-box"><table class="sb-tot" id="sb-totals"><thead><tr><th></th><th class="num">Formula</th>${hasStmt ? '<th class="num">Statement</th><th class="num">Δ</th>' : ''}</tr></thead><tbody>${rows.join('')}</tbody></table>
      ${hasStmt ? `<div class="sb-dim sb-th">✓ within ${fmtMoney(th.ok_abs)} · ≈ within ${fmtMoney(th.close_abs)} or ${th.close_pct}% · ✗ further off${t.formula && t.formula.commission_rounding === 'section' ? ' · commission rounded once, as this statement\'s commission section' : ''}</div>` : ''}
      ${this.fixesListHtml(d)}</div>`;
  }
  // R121: every tested fix for this property, best first (a fix can answer causes in other months too)
  private fixesListHtml(d: any): string {
    const dz = d.diagnose;
    if (!dz) return '';
    if (dz.error) return `<div class="sb-hint" id="sb-why-err">${esc(dz.error)}</div>`;
    const fixes: any[] = Array.isArray(dz.fixes) ? dz.fixes : [];
    const checked = dz.checked && Array.isArray(dz.checked.months) ? dz.checked.months.length : 0;
    if (!fixes.length) return `<div class="sb-hint" id="sb-fixes-none">${checked ? `No setting would bring this property closer on its ${plural(checked, 'statement month', 'statement months')}; what's left is money the booking data doesn't carry (see each cell's reason).` : ''}</div>`;
    return `<div class="sb-fixes" id="sb-fixes"><div class="sb-lbl">Fixes tested on ${plural(checked, 'statement month', 'statement months')}</div>${fixes.map(f => `<div class="sb-fixrow" data-sb-fixrow="${esc(f.id)}"><span><b>${esc(f.label)}</b><span class="sb-dim">${esc(f.gain_text || '')}</span></span><button type="button" class="btn btn-secondary btn-sm" data-sb-fix="${esc(f.id)}"${this.fixLock()}>Fix</button></div>`).join('')}</div>`;
  }
  private expensesHtml(d: any): string {
    const e = d.totals && d.totals.formula && d.totals.formula.expenses;
    if (!e || typeof e !== 'object') return '';
    const items: any[] = Array.isArray(e.items) ? e.items : [];
    const li = items.map(it => {
      if (it.amount == null) return `<li class="sb-missing"><span>${esc(String(it.label || it.kind || 'Work order'))}</span><b>cost not entered yet</b><span class="sb-tag">logged</span></li>`;
      return `<li><span>${esc(String(it.label || it.kind || 'Expense'))}</span><b>${fmtMoney(it.amount)}</b><span class="sb-tag">${esc(it.source === 'qb_maintenance' ? 'logged · QuickBooks' : 'logged')}</span></li>`;
    }).join('');
    // R121: with a statement — what Dash logged next to what the statement charged, by kind (a check on the logging)
    const chk = d.totals && d.totals.expenses_check;
    // R121 review #5: Dash couldn't be read → what it logged is unknown (never "nothing logged" or "not logged in Dash")
    const unread = e.unavailable === true || !!(chk && chk.unavailable === true);
    const unreadNote = unread ? '<div class="sb-hint" id="sb-exp-unread" role="status">Dash couldn\'t be read just now, so what it logged is unknown. Try again in a minute.</div>' : '';
    const kinds: Array<[string, string]> = [['cleaning', 'Cleaning'], ['maintenance', 'Maintenance'], ['supplies', 'Supplies'], ['other', 'Other']];
    const xRow = (k: string, l: string) => {
      const x = chk.by_kind[k] || {};
      if (unread) return `<tr data-sb-xchk="${k}"><th scope="row">${l}</th><td class="num" data-label="Logged"><span class="sb-dim">unknown</span></td><td class="num" data-label="Statement">${fmtMoney(x.statement)}</td><td data-label=""></td></tr>`;
      return `<tr data-sb-xchk="${k}"><th scope="row">${l}</th><td class="num" data-label="Logged">${fmtMoney(x.logged)}</td><td class="num" data-label="Statement">${fmtMoney(x.statement)}</td><td class="${x.match ? 'sb-ok' : 'sb-close'}" data-label="">${x.match ? '✓ logged' : num(x.logged) ? 'partly logged' : 'not logged in Dash'}</td></tr>`;
    };
    const cross = chk ? `<table class="sb-tot sb-xchk" id="sb-exp-check"><thead><tr><th></th><th class="num">Logged in Dash</th><th class="num">Statement</th><th></th></tr></thead><tbody>${kinds.filter(([k]) => num(chk.by_kind[k] && chk.by_kind[k].logged) || num(chk.by_kind[k] && chk.by_kind[k].statement)).map(([k, l]) => xRow(k, l)).join('')}</tbody></table>` : '';
    return `<div class="sb-card-box" id="sb-exp"><div class="sb-lbl">Expenses · ${chk ? 'logged in Dash, next to the statement' : 'logged in Dash'}</div>
      <div class="sb-hint">${esc(EXPLAIN.no_guess)}</div>
      ${unreadNote}
      ${chk ? cross : unread ? '' : `<ul class="sb-exp">${li || '<li class="sb-dim">Nothing logged yet.</li>'}</ul>`}
      ${!chk && !unread && e.complete === false ? '<div class="sb-hint">More owner-billed items may still be logged this month.</div>' : ''}</div>`;
  }
  // every effective setting with where it came from (contract 2.3 `from`)
  private provHtml(d: any): string {
    const s = d.settings;
    if (!s || typeof s !== 'object') return '';
    const from = s.from || {};
    const tag = (txt: string, src: unknown) => `<span class="sb-tag" title="${esc(src ? `from ${FROM_LABEL[String(src)] || src}` : '')}">${esc(txt)}${src ? ` · <em>${esc(this.profileWord(String(src)))}</em>` : ''}</span>`;
    const out: string[] = [];
    if (s.cleaning) out.push(tag(CLEAN_WORDS[s.cleaning] || `cleaning ${s.cleaning}`, from.cleaning));
    out.push(tag(`fee on rents${s.fee_on_cleaning ? ' + cleaning' : ''}${s.fee_on_owner_fees ? ' + owner fees' : ''}`, from.fee_on_cleaning));
    const cr = s.channel_rates || {};
    for (const [k, l] of RATE_CHANNELS) { if (!(k in cr)) continue; out.push(tag(num(cr[k]) != null ? `${l} ${pctTxt(cr[k])}` : `${l}: the booking's own fee`, from[`channel_rates.${k}`] || from[`channel_rate_${k}`] || from.channel_rates)); }
    if (num(s.commission_rate_pct) != null) out.push(tag(`${pctTxt(s.commission_rate_pct)} of ${s.commission_base === 'gross' ? 'gross' : 'net revenue'}`, from.commission_rate_pct || from.commission_rate));
    if (s.commission_base && from.commission_base && from.commission_base !== (from.commission_rate_pct || from.commission_rate)) out.push(tag(`commission on ${s.commission_base === 'gross' ? 'gross' : 'net revenue'}`, from.commission_base));
    if (num(s.min_fee) != null) out.push(tag(`min ${fmtMoney(s.min_fee)} per booking`, from.min_fee));
    if (s.owner_fees) out.push(tag(OWNER_FEE_WORDS[s.owner_fees] || `owner fees ${s.owner_fees}`, from.owner_fees));
    if (s.cancellations) out.push(tag(s.cancellations === 'airbnb_retained' ? 'Airbnb cancellations counted' : 'cancellations not counted', from.cancellations));
    // R121: the company's per-booking fee — the fee in force when each booking was made, else one fee — and where it came from
    if (Array.isArray(s.company_fee_schedule) && s.company_fee_schedule.length > 1) out.push(`<span id="sb-prov-cf">${tag(`company fee ${scheduleText(s.company_fee_schedule)}`, from.company_fee)}</span>`);
    else if (num(s.company_fee) != null) out.push(`<span id="sb-prov-cf">${tag(`company fee ${fmtMoney(s.company_fee)} per booking`, from.company_fee)}</span>`);
    if (s.vrbo_fee_basis === 'guest_total') out.push(tag('Vrbo fee on the guest total', from.vrbo_fee_basis));
    if (num(s.owner_fee_per_booking) != null) out.push(tag(`owner fee ${fmtMoney(s.owner_fee_per_booking)} per booking`, from.owner_fee_per_booking));
    return `<div class="sb-prov" id="sb-prov"><div class="sb-lbl">Settings used</div>${out.join('')}</div>`;
  }
  private bookingsHtml(d: any, th: Thresholds, hasStmt: boolean): string {
    const rows: any[] = Array.isArray(d.rows) ? d.rows : [];
    const cols = BOOKING_KEYS.filter(k => this.cols.has(k));
    const main = rows.filter(r => r.match === 'matched' || (!hasStmt && r.match !== 'statement_only'));
    const stmtOnly = hasStmt ? rows.filter(r => r.match === 'statement_only') : [];
    const formulaOnly = hasStmt ? rows.filter(r => r.match === 'formula_only') : [];
    const stOf = (r: any, k: string): Status | null => {
      if (!hasStmt || !r.statement || !r.formula) return null;
      if (r.status && isStatus(r.status[k])) return r.status[k];
      const f = num(r.formula[k]), s = num(r.statement[k]);
      if (f == null || s == null) return null;
      return statusOf(r.diff && num(r.diff[k]) != null ? Number(r.diff[k]) : f - s, s, th);
    };
    const shown = this.diffOnly && hasStmt ? main.filter(r => cols.some(k => { const s = stOf(r, k); return s && s !== 'ok'; })) : main;
    const when = (r: any) => (r.check_in ? new Date(`${String(r.check_in).slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
    const kind = (r: any) => (r.kind === 'cancelled_retained' || r.kind === 'cancelled' ? ' <span class="sb-tag">cancelled — kept</span>' : r.kind === 'adjustment' ? ' <span class="sb-tag">adjustment</span>' : '');
    // R121: an off / close cell carries its cause (a chip; the sentence is its title)
    const cellWhy = (r: any, k: BookingKey) => {
      const ws: any[] = Array.isArray(r.why) ? r.why.filter((w: any) => w.col === k) : [];
      const w = ws.find(x => x.kind !== 'follows') || ws[0];
      return w ? `<i class="sb-cwhy sb-why-${esc(w.kind || 'data')}" data-sb-cwhy="${esc(w.cause_code)}" title="${esc(w.text || '')}">${esc(CAUSE_SHORT[w.cause_code] || w.cause_code)}</i>` : '';
    };
    const cell = (r: any, k: BookingKey) => {
      const f = r.formula ? num(r.formula[k]) : null, s = r.statement ? num(r.statement[k]) : null;
      const st = stOf(r, k);
      const dl = r.diff && num(r.diff[k]) != null ? num(r.diff[k]) : f != null && s != null ? r2(f - s) : null;
      return `<td class="num sb-cell${st ? ` sb-${st}` : ''}" data-label="${esc(BOOKING_LABEL[k])}" ${st ? `data-sb-status="${st}" title="formula ${esc(fmtMoney(f))} / statement ${esc(fmtMoney(s))} · Δ ${esc(fmtDelta(dl))}"` : ''}><b>${fmtMoney(f)}</b>${hasStmt && r.statement ? `<span>${fmtMoney(s)}</span>` : ''}${st && st !== 'ok' ? cellWhy(r, k) : ''}</td>`;
    };
    // R121: the booking's causes, one line each (a source column once), with its Fix
    const rowWhy = (r: any) => {
      const ws: any[] = Array.isArray(r.why) ? r.why.filter((w: any) => w.col === w.source_col && w.kind !== 'follows') : [];
      if (!ws.length) return '';
      return `<ul class="sb-bwhy" data-sb-bwhy="${esc(r.key || '')}">${ws.map(w => `<li class="sb-why-${esc(w.kind || 'data')}" data-sb-why="${esc(w.cause_code)}"><span>${esc(w.text || '')}</span>${w.fix && w.fix.id ? ` <button type="button" class="sb-fix" data-sb-fix="${esc(w.fix.id)}" title="${esc(`${w.fix.label}. ${w.fix.gain_text || ''}`)}"${this.fixLock()}>Fix</button>` : w.kind === 'data' ? ' <em class="sb-dim">(not a setting)</em>' : ''}</li>`).join('')}</ul>`;
    };
    const table = shown.length ? `<div class="sb-bk-wrap"><table class="sb-bk" id="sb-bk">
        <thead><tr><th>Check-in</th><th>Guest · channel</th>${cols.map(k => `<th class="num">${esc(BOOKING_LABEL[k])}</th>`).join('')}</tr></thead>
        <tbody>${shown.map(r => `<tr data-sb-bk="${esc(r.key || '')}"><td data-label="Check-in" class="sb-when">${esc(when(r))}${r.nights ? ` <span class="sb-dim">${esc(r.nights)} n</span>` : ''}</td><td data-label="Guest · channel" class="sb-guest">${esc(r.guest || '—')}${kind(r)}<div class="sb-dim">${esc(r.channel || '—')}</div>${(Array.isArray(r.notes) && r.notes.length) ? `<div class="sb-hint">${esc(r.notes.join(' · '))}</div>` : ''}</td>${cols.map(k => cell(r, k)).join('')}</tr>${rowWhy(r) ? `<tr class="sb-bwhy-tr" data-sb-bwhy-for="${esc(r.key || '')}"><td colspan="${2 + cols.length}">${rowWhy(r)}</td></tr>` : ''}`).join('')}</tbody>
      </table></div>`
      : `<div class="sb-empty">${main.length ? 'Every booking matches the statement.' : 'No bookings this month.'}</div>`;
    const lst = (id: string, title: string, list: any[], side: 'statement' | 'formula') => list.length ? `<div class="sb-unm" id="${id}"><div class="sb-lbl">${esc(title)} (${list.length})</div><ul>${list.map(r => { const m = r[side] || {}; const w = Array.isArray(r.why) && r.why[0]; return `<li><span>${esc(r.guest || m.guest || '—')}${w ? `<span class="sb-dim sb-unm-why"> · ${esc(w.text || '')}</span>` : ''}</span><span class="sb-dim">${esc(when(r))}</span><b>${fmtMoney(m.gross)}</b></li>`; }).join('')}</ul></div>` : '';
    return `<div class="sb-card-box" id="sb-bookings">
      <div class="sb-bk-h"><div class="sb-lbl">Bookings${hasStmt ? ' · formula / statement' : ''}</div>
        <details class="sb-cols"><summary>Columns</summary><div>${BOOKING_KEYS.map(k => `<label class="sb-check"><input type="checkbox" data-sb-col="${k}" ${this.cols.has(k) ? 'checked' : ''}> ${esc(BOOKING_LABEL[k])}</label>`).join('')}</div></details></div>
      ${table}
      ${lst('sb-stmt-only', 'On the statement, no booking matched', stmtOnly, 'statement')}
      ${lst('sb-formula-only', 'Bookings not on the statement', formulaOnly, 'formula')}
    </div>`;
  }

  private drawFooter(): void {
    const box = this.el?.querySelector<HTMLElement>('#sb-foot');
    if (!box) return;
    const { all, explicit } = this.counts();
    const saved = !!this.draft.id;
    const isDef = saved && this.store().default_rule === this.draft.id;
    const assigned = saved
      ? `Assigned to ${plural(all, 'property', 'properties')}${isDef && all > explicit ? ` (${all - explicit} by default)` : ''}`
      : 'Not assigned yet — saving asks which properties use it';
    box.innerHTML = `
      <div class="sb-foot-l"><span id="sb-assigned">${esc(assigned)}</span>${saved ? ' <button class="btn btn-ghost btn-sm" data-sb="assign">Assign…</button>' : ''}</div>
      <div class="sb-err" id="sb-err" role="alert">${esc(this.errMsg)}</div>
      <div class="sb-foot-r">
        <button class="btn btn-secondary" data-sb="cancel">Cancel</button>
        ${this.orig ? `<button class="btn btn-outline" data-sb="save-new" ${this.busy ? 'disabled' : ''}>Save as new</button>` : ''}
        <button class="btn btn-primary" data-sb="save" ${this.busy || this.builtin() ? 'disabled' : ''} ${this.builtin() ? 'title="The built-in formula can\'t be changed — Save as new makes a copy"' : ''}>${this.busy ? 'Saving…' : this.orig ? 'Save' : 'Save formula'}</button>
      </div>`;
  }

  // ── events (delegated from the overlay — its parts redraw freely) ─────────
  private bind(): void {
    const el = this.el;
    if (!el) return;
    el.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const tabB = t.closest<HTMLElement>('[data-sb-tab]');
      if (tabB) { this.tab = tabB.dataset.sbTab === 'preview' ? 'preview' : 'ladder'; el.dataset.tab = this.tab; el.querySelectorAll<HTMLElement>('[data-sb-tab]').forEach(b => b.setAttribute('aria-selected', String(b.dataset.sbTab === this.tab))); return; }
      const pre = t.closest<HTMLElement>('[data-sb-preset]');
      if (pre) { this.choosePreset(pre.dataset.sbPreset || ''); return; }
      const rc = t.closest<HTMLElement>('[data-sb-ratechip]');
      if (rc) { this.rateMode = 'fixed'; this.raw.commission_rate = rc.dataset.sbRatechip || ''; this.readNumbers(); this.columnsChanged(true); return; }
      const fx = t.closest<HTMLElement>('[data-sb-fix]');   // R121: a tested one-click fix
      if (fx) { if (!(fx as HTMLButtonElement).disabled) this.applyFix(fx.dataset.sbFix || ''); return; }
      const b = t.closest<HTMLElement>('[data-sb]');
      if (!b || (b as HTMLButtonElement).disabled) return;
      void this.action(b.dataset.sb || '');
    });
    el.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Escape') { e.stopPropagation(); this.close(); } });
    el.addEventListener('input', (e) => {
      const t = e.target as HTMLInputElement;
      if (t.id === 'sb-name') { this.draft.name = t.value; this.clearErr(); return; }
      if (t.id === 'sb-desc') { this.draft.description = t.value; return; }
      if (t.id === 'sb-p-search') { this.search = t.value; const s = el.querySelector<HTMLSelectElement>('#sb-p-listing'); if (s) s.innerHTML = this.listingOptions(); return; }
      if (t.id === 'sb-c-commission_rate' || t.id === 'sb-c-min_fee' || t.dataset.sbRate) {
        const k = t.dataset.sbRate ? `rate_${t.dataset.sbRate}` : t.id.slice('sb-c-'.length);
        this.raw[k] = t.value;
        this.readNumbers();
        this.columnsChanged(false);
      }
    });
    el.addEventListener('change', (e) => {
      const t = e.target as HTMLInputElement;
      const c = this.draft.columns;
      if (t.name === 'sb-cleaning') { c.cleaning = t.value as Columns['cleaning']; this.columnsChanged(true); return; }
      if (t.name === 'sb-base') { c.commission_base = t.value as Columns['commission_base']; this.columnsChanged(true); return; }
      if (t.name === 'sb-ratemode') { this.rateMode = t.value === 'fixed' ? 'fixed' : 'property'; this.readNumbers(); this.columnsChanged(true); return; }
      switch (t.id) {
        case 'sb-c-cancellations': c.cancellations = t.checked ? 'airbnb_retained' : 'none'; this.columnsChanged(true); return;
        case 'sb-c-owner_fees': c.owner_fees = t.value as Columns['owner_fees']; this.columnsChanged(true); return;
        case 'sb-c-fee_on_cleaning': c.fee_on_cleaning = t.value === 'true' ? true : t.value === 'false' ? false : 'auto'; this.columnsChanged(true); return;
        case 'sb-c-fee_on_owner_fees': c.fee_on_owner_fees = t.checked; this.columnsChanged(true); return;
        case 'sb-p-listing': {
          if (!t.value || t.value === this.listingId) return;
          // R121 review #20: the draft facts belong to the property on screen — a switch drops them, so it asks
          const facts = this.unsavedFacts();
          if (facts && !confirm(`Switch property? The property facts not saved to ${this.listingName(this.listingId)} yet (${facts}) are discarded.`)) { t.value = this.listingId; return; }
          this.listingId = t.value; this.learn = { busy: false, data: null, err: '', applied: '', applying: false }; this.learnedProfile = null; this.fixFacts = null; this.fixNote = ''; this.pv.data = null; this.pv.ranProfile = null;
          this.statementMonths.clear();   // R119: the ✓ marks are this property's statement months
          // R119 review #13: a month nobody picked follows THIS property's newest statement (not the last one's)
          if (!this.monthPicked) this.month = this.defaultMonth();
          this.drawPreviewBar(); this.drawLearnBanner(); this.drawLeft(); this.drawOut(); this.schedulePreview();
          return;
        }
        case 'sb-p-month':
          if (!/^\d{4}-\d{2}$/.test(t.value)) return;
          this.month = t.value; this.monthPicked = true; this.schedulePreview();
          return;
        case 'sb-p-diff': this.diffOnly = t.checked; this.drawOut(); return;
      }
      if (t.dataset.sbCol) { const k = t.dataset.sbCol as BookingKey; if (t.checked) this.cols.add(k); else this.cols.delete(k); const open = !!el.querySelector('.sb-cols[open]'); this.drawOut(); if (open) el.querySelector('.sb-cols')?.setAttribute('open', ''); }
    });
  }

  // numeric inputs → columns (a bad value is said by its row, and the preview runs without it)
  private readNumbers(): void {
    const c = this.draft.columns; const B = FORMULA_BOUNDS;
    const take = (k: string, [lo, hi]: readonly [number, number], msg: string): number | null => {
      const v = String(this.raw[k] ?? '').trim();
      delete this.bad[k];
      if (v === '') return null;
      const n = Number(v);
      if (!Number.isFinite(n) || n < lo || n > hi) { this.bad[k] = msg; return null; }
      return n;
    };
    const rates: Record<string, number | null> = {}; let any = false;
    for (const [k, l] of RATE_CHANNELS) { const v = take(`rate_${k}`, B.channel_rate, `${l} rate: a % from ${B.channel_rate[0]} to ${B.channel_rate[1]}, or blank for the shared rate.`); rates[k] = v; if (v != null) any = true; }
    c.channel_rates = any ? rates : null;
    c.min_fee = take('min_fee', B.min_fee, `Minimum per booking: a dollar amount from 0 to ${B.min_fee[1].toLocaleString()}, or blank.`);
    if (this.rateMode === 'fixed') c.commission_rate = take('commission_rate', B.commission_rate, `Commission rate: a % from ${B.commission_rate[0]} to ${B.commission_rate[1]}.`);
    else { c.commission_rate = null; delete this.bad.commission_rate; }
  }

  private columnsChanged(redraw: boolean): void {
    this.clearErr();
    if (redraw) this.drawLeft();
    else {
      // typing in a number: only that row's error line follows (the input keeps focus)
      for (const key of ['channel_fee', 'commission']) {
        const row = this.el?.querySelector<HTMLElement>(`[data-sb-row="${key}"]`);
        if (!row) continue;
        const msg = this.rowErr(key);
        row.classList.toggle('sb-row-bad', !!msg);
        let line = row.querySelector<HTMLElement>('.sb-row-err');
        if (msg && !line) { line = document.createElement('div'); line.className = 'sb-row-err'; line.setAttribute('role', 'alert'); row.querySelector('.sb-row-c')?.appendChild(line); }
        if (line) { if (msg) line.textContent = msg; else line.remove(); }
      }
    }
    this.drawPresets();
    this.schedulePreview();
  }

  private choosePreset(id: string): void {
    if (id === 'custom') return;   // "Custom" lights up by itself when the shape matches no preset
    const p = this.presets().find(x => String(x.id) === id);
    if (!p) return;
    const before = this.draft.columns.channel_rates;
    this.draft.columns = applyPreset(this.draft.columns, p, this.presets());
    // R119 review #15: the server's presets carry FULL columns (channel_rates: null on all but
    // E) — only a preset that NAMES rates (E: none) sets the inputs (or one leaving E takes its
    // zeros away); rates the office typed survive any other chip
    const after = this.draft.columns.channel_rates;
    if (!sameRates(before, after)) for (const [k] of RATE_CHANNELS) { const v = after ? after[k] : null; this.raw[`rate_${k}`] = v == null ? '' : String(v); delete this.bad[`rate_${k}`]; }
    this.columnsChanged(true);
  }

  private async action(a: string): Promise<void> {
    switch (a) {
      case 'close': case 'cancel': this.close(); return;
      case 'save': return this.save(false);
      case 'save-new': return this.save(true);
      case 'assign': if (this.draft.id) this.host.openAssign(this.draft.id); return;
      case 'facts': if (this.listingId) this.host.editFacts(this.listingId); return;
      case 'learn': return this.runLearn();
      case 'learn-apply': return this.applyLearn();
      case 'retry': this.schedulePreview(true); return;
      case 'guard-foc': this.draft.columns.fee_on_cleaning = true; this.fixNote = 'Applied: the channel fee is charged on rents + cleaning.'; this.columnsChanged(true); return;
      case 'save-facts': return this.saveFacts();
    }
  }

  // ── R121: one-click fixes (each tested on every statement month by the server) ──
  // A fix patches the DRAFT: columns into the formula (Save keeps them), facts into the draft facts
  // the preview runs with ("Save facts" writes them to the property — POST /profile).
  // R121 review #11 (two Fix clicks on one reply undid each other: a channel-rate patch carries EVERY
  // channel's rate as the draft had it, nulls included, and the reply's Fix buttons stayed live while
  // the preview re-ran): a Fix applies only while its reply is the one on screen (none while the draft
  // is being re-tested — the buttons are disabled until the new reply), and a channel-rate patch
  // changes only the channels it CHANGES against the facts that reply ran with.
  private applyFix(id: string): void {
    if (this.pv.pending) return;
    const d = this.pv.data;
    const fx = d && d.diagnose && Array.isArray(d.diagnose.fixes) ? d.diagnose.fixes.find((f: any) => f && f.id === id) : null;
    if (!fx || !fx.patch || typeof fx.patch !== 'object') return;
    const p = fx.patch;
    if (p.columns && typeof p.columns === 'object') {
      this.draft.columns = normColumns({ ...this.draft.columns, ...p.columns });
      if ('channel_rates' in p.columns) for (const [k] of RATE_CHANNELS) { const v = this.draft.columns.channel_rates ? this.draft.columns.channel_rates[k] : null; this.raw[`rate_${k}`] = v == null ? '' : String(v); }
    }
    if (p.profile && typeof p.profile === 'object') {
      const facts: Record<string, any> = { ...p.profile };
      let ran: Record<string, number | null> | null = null;
      if (facts.channel_rates && typeof facts.channel_rates === 'object') {
        // the channel rates the reply ran with: the draft's whole set when it sent one, else the stored set
        const rp: any = this.pv.ranProfile;
        ran = pickRates(rp && rp.channel_rates && typeof rp.channel_rates === 'object' ? rp.channel_rates : (this.storedProfile() || {}).channel_rates);
        const pr = pickRates(facts.channel_rates);
        const changed: Record<string, number | null> = {};
        // a Vrbo fee on the guest total is a pair (its basis + its rate): the rate goes with the basis
        for (const [k] of RATE_CHANNELS) if (k in facts.channel_rates && (pr[k] !== ran[k] || (k === 'vrbo' && 'vrbo_fee_basis' in facts))) changed[k] = pr[k];
        if (Object.keys(changed).length) facts.channel_rates = changed; else delete facts.channel_rates;
      }
      const lp: Record<string, any> = { ...(this.learnedProfile || {}) };
      const ff: Record<string, any> = { ...(this.fixFacts || {}) };
      for (const [k, v] of Object.entries(facts)) {
        if (k === 'channel_rates') {
          // the draft carries the WHOLE set (the preview replaces the stored set with it); fixFacts only what a Fix changed
          lp.channel_rates = { ...(lp.channel_rates && typeof lp.channel_rates === 'object' ? pickRates(lp.channel_rates) : ran), ...(v as object) };
          ff.channel_rates = { ...(ff.channel_rates || {}), ...(v as object) };
        } else { lp[k] = v; ff[k] = v; }
      }
      this.learnedProfile = Object.keys(lp).length ? lp : null;
      this.fixFacts = Object.keys(ff).length ? ff : null;
    }
    this.fixNote = `Applied: ${fx.label}. ${fx.gain_text ? `Tested: ${fx.gain_text}.` : ''}`;
    this.columnsChanged(true);
    this.drawOut();
  }
  private async saveFacts(): Promise<void> {
    const facts = this.fixFacts;
    if (!facts || !this.listingId || this.busy) return;
    const lid = this.listingId;
    // POST /profile stores a profile's channel_rates WHOLE: the stored rates, with the channels a Fix changed
    // (never the rates Learn found but nobody applied: those stay in the draft)
    const body: Record<string, any> = { ...facts };
    if (facts.channel_rates && typeof facts.channel_rates === 'object') body.channel_rates = { ...pickRates((this.storedProfile() || {}).channel_rates), ...(facts.channel_rates as object) };
    this.busy = true;
    try {
      const r = await fetch('/api/owner-finance/profile', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ listing_id: lid, profile: body }) });
      const j = await r.json().catch(() => ({}));
      if (!this.isOpen()) return;
      this.busy = false;
      if (!r.ok) { this.fixNote = `Facts not saved: ${(j && j.error) || `HTTP ${r.status}`}.`; this.drawOut(); return; }
      this.host.adoptStore(j);
      // R121 review #21: they are the property's facts now — the draft drops only what was saved (a key
      // still holding the Fix's value); Learn's other facts stay in it, channel rates as a whole set
      const lp = this.learnedProfile ? { ...this.learnedProfile } as Record<string, any> : null;
      if (lp) {
        const stored: any = (j && j.profile && typeof j.profile === 'object') ? j.profile : this.storedProfile() || {};
        for (const k of Object.keys(facts)) {
          if (k === 'channel_rates') { if (sameVal(pickRates(lp.channel_rates), pickRates(stored.channel_rates))) delete lp.channel_rates; }
          else if (sameVal(lp[k], (facts as any)[k])) delete lp[k];
        }
      }
      this.learnedProfile = lp && Object.keys(lp).length ? lp : null;
      this.fixFacts = null;
      this.fixNote = `✓ Facts saved to ${this.listingName(lid)}.`;
      this.host.factsSaved?.(lid);   // R121 review #24: a pending "Match the statements" row re-checks what Apply does
      this.drawFooter(); this.schedulePreview(true);
    } catch (e: any) {
      if (!this.isOpen()) return;
      this.busy = false; this.fixNote = `Facts not saved: ${(e && e.message) || 'network error'}.`; this.drawOut();
    }
  }

  // ── preview: POST /preview with the DRAFT (300 ms debounce, abort, sequence) ──
  // R119 review #13: a change makes the reply in flight stale AT ONCE (it used to stay current
  // through the 300 ms debounce: a property switch took the previous property's reply — its
  // statement months, its newest-statement jump). The sequence moves and the old request is
  // aborted here, not when the debounced one starts; "Updating…" stays until the newest reply.
  private schedulePreview(now = false): void {
    if (!this.isOpen()) return;
    clearTimeout(this.pv.timer);
    const seq = ++this.pv.seq;
    try { this.pv.ctrl?.abort(); } catch { /* gone */ }
    this.pv.ctrl = null;
    if (!this.listingId) { this.pv.pending = false; this.drawUpdating(); this.drawOut(); return; }
    this.pv.pending = true; this.drawUpdating();
    this.pv.timer = setTimeout(() => { void this.runPreview(seq); }, now ? 0 : DEBOUNCE_MS);
  }
  private async runPreview(seq: number): Promise<void> {
    if (seq !== this.pv.seq || !this.isOpen()) return;   // re-scheduled meanwhile
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    this.pv.ctrl = ctrl;
    const lid = this.listingId, month = this.month;
    // the DRAFT; an untouched stored formula goes by id (the same thing — and a
    // legacy rule then runs exactly as stored, its 'never' cleaning included)
    const body: Record<string, unknown> = { listing_id: lid, month, diagnose: true };   // R121: why each cell is off + tested fixes
    if (this.orig && !this.dirty()) body.formula_id = String(this.orig.id); else body.formula = this.draftFormula();
    if (this.learnedProfile) body.profile = this.learnedProfile;
    const sent = this.learnedProfile ? JSON.parse(JSON.stringify(this.learnedProfile)) as Record<string, unknown> : null;   // R121 review #11: what this reply's Fix patches are against
    try {
      const r = await fetch('/api/owner-finance/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: ctrl ? ctrl.signal : undefined });
      const j = await r.json().catch(() => ({}));
      if (seq !== this.pv.seq || !this.isOpen()) return;   // a newer request owns the screen
      // …and only a reply for the property + month on screen is this one's (its statement months
      // are that property's). The sequence already guarantees it; a reply naming another one
      // (never sent by the server) is refused in words — it must not hang "Updating…" either
      if (lid !== this.listingId || month !== this.month) { this.schedulePreview(true); return; }
      this.pv.pending = false;
      const forThis = !!(j && j.listing && String(j.listing.id) === lid && String(j.month) === month);
      if (!r.ok) { this.pv.err = String((j && j.error) || `Couldn't run the preview (HTTP ${r.status}).`); }
      else if (!forThis) { this.pv.err = 'The preview came back for another property or month.'; }
      else {
        this.pv.err = ''; this.pv.data = j; this.pv.ranProfile = sent;
        const m = month;
        if (j.statement_source || (j.totals && j.totals.statement)) this.statementMonths.add(m);
        for (const x of (Array.isArray(j.statement_months) ? j.statement_months : [])) if (/^\d{4}-\d{2}$/.test(String(x))) this.statementMonths.add(String(x));
        // R119: opened without a month (Preview / Edit / a property switch), the
        // preview lands on the property's newest statement month, not an empty forecast
        const latest = [...this.statementMonths].sort().pop();
        if (!this.monthPicked && latest && latest !== this.month && !j.statement_source) {
          this.month = latest;   // not "picked": a property switch follows that home's newest statement
          const ms0 = this.el?.querySelector<HTMLSelectElement>('#sb-p-month'); if (ms0) ms0.innerHTML = this.monthOptions();
          this.schedulePreview(true);
          return;
        }
        const ms = this.el?.querySelector<HTMLSelectElement>('#sb-p-month'); if (ms) ms.innerHTML = this.monthOptions();
      }
    } catch (e: any) {
      if (seq !== this.pv.seq || !this.isOpen() || (e && e.name === 'AbortError')) return;
      this.pv.pending = false;
      this.pv.err = `Couldn't run the preview — ${(e && e.message) || 'network error'}.`;
    }
    this.drawUpdating(); this.drawOut();
  }

  // ── Learn (dry run) → controls + evidence; Apply = POST /learn/apply ──────
  private async runLearn(): Promise<void> {
    if (!this.listingId || this.learn.busy) return;
    const lid = this.listingId;
    this.learn = { busy: true, data: null, err: '', applied: '', applying: false };
    this.drawPreviewBar(); this.drawLearnBanner();
    try {
      const r = await fetch('/api/owner-finance/learn', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ listing_id: lid }) });
      const j = await r.json().catch(() => ({}));
      if (!this.isOpen() || lid !== this.listingId) return;
      if (!r.ok) { this.learn = { busy: false, data: null, err: `Couldn't learn from the statements — ${(j && j.error) || `HTTP ${r.status}`}.`, applied: '', applying: false }; return; }
      this.learn = { busy: false, data: j, err: '', applied: '', applying: false };
      if (j.columns && typeof j.columns === 'object') {
        this.draft.columns = normColumns({ ...this.draft.columns, ...j.columns });
        const c = this.draft.columns;
        this.rateMode = c.commission_rate != null ? 'fixed' : 'property';
        this.raw.commission_rate = c.commission_rate != null ? String(c.commission_rate) : '';
        this.raw.min_fee = c.min_fee != null ? String(c.min_fee) : '';
        for (const [k] of RATE_CHANNELS) this.raw[`rate_${k}`] = c.channel_rates && c.channel_rates[k] != null ? String(c.channel_rates[k]) : '';
        this.bad = {};
      }
      for (const m of (Array.isArray(j.months) ? j.months : [])) if (/^\d{4}-\d{2}$/.test(String(m))) this.statementMonths.add(String(m));
      const pf = j.profile_facts && typeof j.profile_facts === 'object' ? Object.fromEntries(Object.entries(j.profile_facts).filter(([, v]) => v != null)) : {};
      // R121 review #13/#21 (Learn used to REPLACE the draft facts: a Fix's facts left the preview while
      // the banner still offered "Save facts" for them): Learn's facts go UNDER the Fix facts — the
      // preview runs what the banner says, and Learn's Assign saves them all
      this.learnedProfile = this.withFixFacts(pf);
      // Learn replaced the formula's columns too: "Applied … Tested …" was about the draft before it
      this.fixNote = this.fixFacts ? 'Learn\'s facts are in this draft, with the facts from your Fix on top of them.' : '';
      this.drawLeft(); this.drawPresets();
      this.schedulePreview();
    } catch (e: any) {
      if (this.isOpen()) this.learn = { busy: false, data: null, err: `Couldn't learn from the statements — ${(e && e.message) || 'network error'}.`, applied: '', applying: false };
    } finally {
      if (this.isOpen()) { this.learn.busy = false; this.drawPreviewBar(); this.drawLearnBanner(); }
    }
  }
  private async applyLearn(): Promise<void> {
    const d = this.learn.data;
    if (!d || this.learn.applying || !this.listingId) return;
    const lid = this.listingId; const m = d.match || {};
    const body: Record<string, unknown> = { listing_id: lid, profile: this.learnedProfile || {} };
    if (m.formula_id) body.formula_id = m.formula_id;
    else {
      const f = this.draftFormula(); delete f.id;
      const same = this.orig && f.name === String(this.orig.name);
      f.name = (!f.name || same ? String(m.name || `Learned from ${this.listingName(lid)}`) : f.name).slice(0, FORMULA_BOUNDS.name_max);
      body.formula = f;
    }
    this.learn.applying = true; this.drawLearnBanner();
    try {
      const r = await fetch('/api/owner-finance/learn/apply', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!this.isOpen()) return;
      if (!r.ok) { this.learn.applying = false; this.learn.err = `Not saved — ${(j && j.error) || `HTTP ${r.status}`}.`; this.learn.data = null; this.drawLearnBanner(); return; }
      this.host.adoptStore(j);
      const s = this.store();
      const fid = (s.assign && s.assign[lid]) || m.formula_id || '';
      const f = (Array.isArray(s.rules) ? s.rules : []).find((x: any) => String(x.id) === String(fid));
      const name = f ? String(f.name) : String(m.name || fid);
      this.learnedProfile = null; this.fixFacts = null;   // saved with Learn's facts (the draft facts are one set — review #13)
      this.host.factsSaved?.(lid);   // R121 review #24: a pending "Match the statements" row re-checks what Apply does
      // the builder now shows the formula this property uses — saved, with the learned shape
      if (f) this.open({ formula: f, mode: this.tab === 'preview' ? 'preview' : 'edit', listingId: lid, month: this.month });
      this.learn.applied = `✓ ${this.listingName(lid)} now uses “${name}”, and its facts (company fee and when it changed, taxes, rates) are saved.`;
      this.drawLearnBanner(); this.drawFooter();
    } catch (e: any) {
      if (!this.isOpen()) return;
      this.learn.applying = false; this.learn.err = `Not saved — ${(e && e.message) || 'network error'}.`; this.learn.data = null; this.drawLearnBanner();
    }
  }

  // ── save: POST /formulas (one formula; no whole-store version) ────────────
  private validate(): { msg: string; field: string } | null {
    const d = this.draft; const B = FORMULA_BOUNDS;
    const name = d.name.trim();
    if (!name) return { msg: 'Give the formula a name.', field: 'name' };
    if (name.length > B.name_max) return { msg: `Name: at most ${B.name_max} characters.`, field: 'name' };
    if (d.description.trim().length > B.description_max) return { msg: `Description: at most ${B.description_max} characters.`, field: 'description' };
    if (d.columns.cleaning === 'auto_not_gross') return { msg: 'Cleaning: pick where it goes — “Automatic, never in Gross” was the setting before R119 and isn\'t one of the four.', field: 'cleaning' };
    const k = Object.keys(this.bad)[0];
    if (k) return { msg: this.bad[k], field: k };
    if (this.rateMode === 'fixed' && d.columns.commission_rate == null) return { msg: 'Commission: pick a fixed rate, or choose “each property\'s rate”.', field: 'commission_rate' };
    return null;
  }
  private setErr(msg: string, fields: string[] = []): void {
    this.errMsg = msg;
    const el = this.el?.querySelector<HTMLElement>('#sb-err'); if (el) el.textContent = msg;
    const rowOf = (f: string): string => {
      const k = f.replace(/^columns\./, '');
      if (k === 'name' || k === 'description') return '';
      if (k === 'cleaning') return 'cleaning_col';
      if (k.startsWith('rate_') || k.startsWith('channel_rates') || k === 'fee_on_cleaning' || k === 'fee_on_owner_fees') return 'channel_fee';
      if (k === 'commission_rate' || k === 'commission_base' || k === 'min_fee') return 'commission';
      if (k === 'owner_fees') return 'resolutions';
      if (k === 'cancellations') return 'rents';
      return '';
    };
    this.el?.querySelectorAll<HTMLElement>('.sb-row').forEach(r => r.classList.remove('sb-row-bad'));
    for (const f of fields) {
      if (/^(name|description)$/.test(f)) { const i = this.el?.querySelector<HTMLInputElement>(f === 'name' ? '#sb-name' : '#sb-desc'); i?.classList.add('sb-in-bad'); i?.focus(); continue; }
      const row = rowOf(f); if (row) this.el?.querySelector(`[data-sb-row="${row}"]`)?.classList.add('sb-row-bad');
    }
  }
  private clearErr(): void {
    if (!this.errMsg) return;
    this.errMsg = ''; const el = this.el?.querySelector<HTMLElement>('#sb-err'); if (el) el.textContent = '';
    this.el?.querySelectorAll('.sb-in-bad').forEach(n => n.classList.remove('sb-in-bad'));
  }
  private async save(asNew: boolean): Promise<void> {
    if (this.busy) return;
    const bad = this.validate();
    if (bad) {
      if (bad.field !== 'name' && bad.field !== 'description' && this.tab !== 'ladder') { this.tab = 'ladder'; if (this.el) this.el.dataset.tab = 'ladder'; }
      this.setErr(bad.msg, [bad.field]); return;
    }
    const isNew = asNew || !this.draft.id;
    if (!isNew && this.builtin()) return;
    // R121 review #20: a save closes the builder (the page's saved() forces it) — facts a Fix (or Learn)
    // put in the draft would go with it, unsaved and unsaid. Ask first; Cancel keeps everything open.
    const facts = this.unsavedFacts();
    if (facts) {
      const keep = [this.fixFacts && Object.keys(this.fixFacts).length ? '"Save facts"' : '', this.learnedOnly() ? 'Learn\'s Assign' : ''].filter(Boolean).join(' or ');
      if (!confirm(`The property facts in this draft aren't saved to ${this.listingName(this.listingId)} yet (${facts}). Saving the formula closes the builder without them${keep ? `; ${keep} keeps them` : ''}. Save the formula anyway?`)) return;
    }
    const f = this.draftFormula();
    if (isNew) { delete f.id; if (this.orig && f.name === String(this.orig.name)) f.name = this.copyName(f.name); }
    if (!isNew) {
      const n = this.counts().all;
      // R119: an edit reaches every property on the formula (today's WCH_INCLUDE_CLEAN_20 edit moved 20) — say how many first
      if (n > 1 && !confirm(`Save “${f.name}”? It is used by ${n} properties — the owner portal of every one of them changes.`)) return;
    }
    this.busy = true; this.drawFooter();
    try {
      const r = await fetch('/api/owner-finance/formulas', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ formula: f }) });
      const j = await r.json().catch(() => ({}));
      if (!this.isOpen()) return;
      if (!r.ok) {
        this.busy = false; this.drawFooter();
        this.setErr(String((j && j.error) || `Not saved (HTTP ${r.status}).`), Array.isArray(j && j.fields) ? j.fields.map(String) : []);
        return;
      }
      this.host.adoptStore(j);
      const saved = (j && j.formula) || f;
      this.snap = JSON.stringify(this.draft);   // nothing left to discard
      this.busy = false;
      this.host.saved(saved, isNew, String(saved.name || f.name));
    } catch (e: any) {
      if (!this.isOpen()) return;
      this.busy = false; this.drawFooter();
      this.setErr(`Not saved — ${(e && e.message) || 'network error'}.`);
    }
  }
}

// ── styles (scoped to the overlay; colors only through the :root tokens) ────
const STYLE = `
.sb-overlay { position:fixed; inset:0; z-index:1000; background:rgba(15,23,42,.45); overflow-y:auto; overflow-x:hidden; padding:24px 16px; box-sizing:border-box; }
.sb-overlay *, .sb-overlay *::before, .sb-overlay *::after { box-sizing:border-box; }
.sb-panel { max-width:1280px; margin:0 auto; background:var(--card); border-radius:14px; box-shadow:var(--shadow-pop); display:flex; flex-direction:column; min-width:0; }
.sb-hdr { display:flex; gap:12px; align-items:flex-start; padding:16px 20px; border-bottom:1px solid var(--border); }
.sb-hdr-main { flex:1; min-width:0; display:flex; flex-direction:column; gap:8px; }
.sb-hdr-top { display:flex; gap:10px; align-items:baseline; flex-wrap:wrap; }
.sb-hdr-top h3 { font-size:16px; font-weight:600; margin:0; color:var(--text); }
.sb-names { display:flex; gap:8px; flex-wrap:wrap; }
.sb-in { min-height:34px; padding:6px 10px; border:1px solid var(--border); border-radius:7px; font:inherit; font-size:13px; background:var(--card); color:var(--text); max-width:100%; }
.sb-in:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
.sb-in-bad { border-color:var(--danger-text); }
.sb-in-s { width:96px; }
.sb-in-m { width:220px; }
.sb-in-auto { width:auto; min-width:120px; }
.sb-name { flex:2 1 260px; font-weight:600; min-width:0; }
.sb-desc { flex:3 1 260px; min-width:0; }
.sb-presets { display:flex; gap:6px; flex-wrap:wrap; }
.sb-chip { font:inherit; font-size:12px; font-weight:600; min-height:30px; padding:4px 12px; border-radius:var(--radius-pill); border:1px solid var(--border); background:var(--card); color:var(--text-muted); cursor:pointer; }
.sb-chip:hover { border-color:var(--border-strong); color:var(--text); }
.sb-chip.on { background:var(--accent-soft); border-color:var(--accent-border); color:var(--accent-hover); }
.sb-chip[data-sb-preset="custom"] { cursor:default; }
.sb-chip sup { font-size:9px; margin-left:3px; color:var(--text-muted); }
.sb-chip-s { min-height:28px; padding:2px 9px; font-size:11.5px; }
.sb-banner { margin:10px 20px 0; padding:9px 12px; border-radius:8px; font-size:12.5px; line-height:1.45; background:var(--bg-subtle); border:1px solid var(--border); color:var(--text); }
.sb-right .sb-banner { margin:8px 0 0; }
.sb-banner-warn { background:var(--warning-soft); border-color:var(--warning-border); color:var(--warning-text); }
.sb-banner-ok { background:var(--success-soft); border-color:var(--success-border); color:var(--success-text); }
.sb-banner-bad { background:var(--danger-soft); border-color:var(--danger-border); color:var(--danger-text); }
.sb-banner-learn { background:var(--accent-soft); border-color:var(--accent-border); }
.sb-banner-learn > div + div { margin-top:4px; }
.sb-tabs { display:none; gap:6px; padding:10px 16px 0; }
.sb-tabs button { flex:1; font:inherit; font-size:13px; font-weight:600; min-height:38px; border:1px solid var(--border); border-radius:8px; background:var(--card); color:var(--text-muted); cursor:pointer; }
.sb-tabs button[aria-selected="true"] { background:var(--accent-soft); border-color:var(--accent-border); color:var(--accent-hover); }
.sb-body { display:grid; grid-template-columns:minmax(340px, 420px) minmax(0, 1fr); gap:0; min-width:0; }
.sb-left { border-right:1px solid var(--border); padding:12px 16px 16px; min-width:0; }
.sb-right { padding:12px 18px 16px; min-width:0; }
.sb-pane-h { display:flex; gap:10px; align-items:baseline; justify-content:space-between; margin-bottom:6px; }
.sb-pane-h h4 { font-size:13px; font-weight:600; margin:0; color:var(--text); }
.sb-dim { font-size:11.5px; color:var(--text-muted); }
.sb-hint { font-size:11px; color:var(--text-muted); margin-top:4px; line-height:1.4; }
.sb-lbl { display:block; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin:8px 0 4px; }
.sb-row-c > .sb-lbl:first-child { margin-top:0; }
.sb-row { display:grid; grid-template-columns:128px minmax(0, 1fr); gap:10px; padding:10px 0; border-top:1px solid var(--border-soft); }
.sb-row:first-of-type { border-top:none; }
.sb-row-h { display:flex; gap:8px; min-width:0; }
.sb-n { flex:none; width:20px; height:20px; border-radius:50%; background:var(--bg-subtle); border:1px solid var(--border); font-size:10.5px; font-weight:600; color:var(--text-muted); display:inline-flex; align-items:center; justify-content:center; }
.sb-row-l { font-size:12.5px; font-weight:600; color:var(--text); }
.sb-row-d { font-size:11px; color:var(--text-muted); line-height:1.35; margin-top:2px; }
.sb-row-c { min-width:0; font-size:12.5px; }
.sb-computed { background:var(--bg-subtle); margin:0 -16px; padding:8px 16px; }
.sb-computed .sb-row-l { color:var(--text-muted); }
.sb-row-bad .sb-row-l { color:var(--danger-text); }
.sb-row-err { font-size:11.5px; color:var(--danger-text); margin-top:5px; }
.sb-check { display:inline-flex; gap:6px; align-items:center; font-size:12.5px; color:var(--text); cursor:pointer; min-height:30px; margin-right:10px; }
.sb-check input { accent-color:var(--accent); width:15px; height:15px; }
.sb-locked { color:var(--text-muted); cursor:default; }
.sb-on-row { display:flex; flex-wrap:wrap; align-items:center; }
.sb-cards { display:grid; gap:6px; }
.sb-card { display:flex; gap:8px; align-items:flex-start; padding:8px 10px; border:1px solid var(--border); border-radius:8px; cursor:pointer; background:var(--card); }
.sb-card input { margin-top:2px; accent-color:var(--accent); }
.sb-card b { display:block; font-size:12.5px; font-weight:600; color:var(--text); }
.sb-card span span { display:block; font-size:11px; color:var(--text-muted); }
.sb-card.on { border-color:var(--accent-border); background:var(--accent-soft); }
.sb-rates { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:6px 10px; margin-top:8px; }
.sb-rates label { display:flex; flex-direction:column; gap:2px; font-size:11px; color:var(--text-muted); min-width:0; }
.sb-rates input { width:100%; }
.sb-seg { display:flex; flex-wrap:wrap; gap:4px; max-width:100%; }
.sb-seg-b { position:relative; display:inline-flex; align-items:center; min-height:32px; padding:4px 10px; font-size:11.5px; font-weight:600; color:var(--text-muted); cursor:pointer; border:1px solid var(--border); border-radius:8px; background:var(--card); }
.sb-seg-b input { position:absolute; opacity:0; pointer-events:none; width:1px; height:1px; }
.sb-seg-b.on { background:var(--accent-soft); border-color:var(--accent-border); color:var(--accent-hover); }
.sb-seg-b:focus-within { box-shadow:var(--focus-ring); }
.sb-ratechips { display:flex; flex-wrap:wrap; gap:5px; align-items:center; margin:4px 0 2px; }
.sb-dimmed .sb-chip { opacity:.55; }
.sb-explain { font-size:11.5px; color:var(--text-muted); line-height:1.45; display:flex; flex-direction:column; gap:2px; min-width:0; }
.sb-explain b { color:var(--text); font-weight:600; }
.sb-explain-d summary { cursor:pointer; color:var(--accent); min-height:28px; display:inline-flex; align-items:center; }
.sb-explain-d p { margin:2px 0 4px; }
.sb-guard { margin-top:6px; padding:7px 9px; border-radius:8px; font-size:11.5px; line-height:1.45; background:var(--warning-soft); border:1px solid var(--warning-border); color:var(--warning-text); }
.sb-guard .btn { margin-top:4px; }
.sb-whys { display:flex; flex-wrap:wrap; gap:3px; margin-top:3px; justify-content:flex-end; }
.sb-why { display:inline-flex; align-items:center; gap:4px; max-width:100%; padding:1px 6px; border-radius:var(--radius-pill); font-size:10.5px; font-weight:600; line-height:1.6; white-space:normal; text-align:left; background:var(--bg-subtle); border:1px solid var(--border); color:var(--text); }
.sb-why-setting { background:var(--accent-soft); border-color:var(--accent-border); color:var(--accent-hover); }
.sb-why-follows { color:var(--text-muted); font-weight:500; }
.sb-fix { font:inherit; font-size:10.5px; font-weight:700; min-height:22px; padding:0 7px; border-radius:var(--radius-pill); border:1px solid var(--accent-border); background:var(--card); color:var(--accent-hover); cursor:pointer; }
.sb-fix:hover { background:var(--accent); color:var(--card); }
.sb-fix:disabled, .sb-fix:disabled:hover { opacity:.45; cursor:progress; background:var(--card); color:var(--accent-hover); }
.sb-cwhy { display:block; font-style:normal; font-size:10px; font-weight:600; margin-top:2px; opacity:.9; white-space:normal; }
.sb-bwhy { list-style:none; margin:0; padding:0; font-size:11.5px; line-height:1.45; display:flex; flex-direction:column; gap:3px; }
.sb-bwhy li { display:flex; gap:8px; align-items:center; justify-content:space-between; padding:3px 8px; border-radius:6px; background:var(--bg-subtle); color:var(--text); overflow-wrap:anywhere; }
.sb-bwhy li > span { flex:1; min-width:0; }
.sb-bk tr.sb-bwhy-tr td { padding:0 4px 8px 4px; border-bottom:1px solid var(--border); }
.sb-bk tbody tr:has(+ tr.sb-bwhy-tr) td { border-bottom:none; }
.sb-bwhy li.sb-why-setting { background:var(--accent-soft); }
.sb-fixes { margin-top:8px; border-top:1px solid var(--border-soft); padding-top:6px; }
.sb-fixrow { display:flex; gap:8px; align-items:center; justify-content:space-between; padding:4px 0; flex-wrap:wrap; }
.sb-fixrow > span { flex:1 1 200px; min-width:0; display:flex; flex-direction:column; font-size:12px; }
.sb-xchk { margin-top:4px; }
.sb-unm-why { white-space:normal; }
.sb-ev { display:inline-flex; margin:4px 4px 0 0; padding:1px 8px; border-radius:var(--radius-pill); font-size:10.5px; font-weight:600; background:var(--accent-soft); color:var(--accent-hover); border:1px solid var(--accent-border); white-space:nowrap; }
.sb-p-bar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:8px; }
.sb-p-search { flex:1 1 150px; min-width:0; }
.sb-p-listing { flex:2 1 200px; min-width:0; }
.sb-p-month { flex:0 1 140px; min-width:0; }
.sb-p-head { font-size:13px; margin:10px 0 6px; color:var(--text); }
.sb-card-box { border:1px solid var(--border); border-radius:10px; padding:10px 12px; margin-top:10px; min-width:0; }
.sb-tot { width:100%; border-collapse:collapse; font-size:12.5px; font-variant-numeric:tabular-nums; }
.sb-tot th, .sb-tot td { padding:5px 8px; border-bottom:1px solid var(--border-soft); text-align:left; }
.sb-tot thead th { font-size:10.5px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); font-weight:600; }
.sb-tot .num, .sb-bk .num { text-align:right; white-space:nowrap; }
.sb-tot tbody th { font-weight:500; color:var(--text); }
.sb-tot tr.sb-sub th, .sb-tot tr.sb-sub td { font-weight:700; background:var(--bg-subtle); }
.sb-th { margin-top:6px; }
.sb-d { border-radius:6px; }
.sb-ok { background:var(--success-soft); color:var(--success-text); }
.sb-close { background:var(--warning-soft); color:var(--warning-text); }
.sb-off { background:var(--danger-soft); color:var(--danger-text); }
.sb-exp { list-style:none; margin:4px 0 0; padding:0; font-size:12.5px; }
.sb-exp li { display:flex; gap:8px; align-items:center; flex-wrap:wrap; padding:4px 0; border-bottom:1px solid var(--border-soft); }
.sb-exp li > span:first-child { flex:1; min-width:0; }
.sb-missing { color:var(--warning-text); }
.sb-prov { margin-top:10px; display:flex; flex-wrap:wrap; gap:5px; align-items:center; }
.sb-prov .sb-lbl { width:100%; margin:0 0 2px; }
.sb-tag { display:inline-block; padding:2px 8px; border-radius:var(--radius-pill); font-size:11px; line-height:1.5; background:var(--bg-subtle); border:1px solid var(--border); color:var(--text); max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; vertical-align:middle; }
.sb-tag em { font-style:normal; color:var(--text-muted); }
.sb-bk-h { display:flex; justify-content:space-between; align-items:center; gap:4px 8px; flex-wrap:wrap; }
.sb-cols { min-width:0; max-width:100%; }
.sb-cols[open] { flex-basis:100%; }
.sb-cols:not([open]) > div { display:none; }
.sb-bk-h .sb-lbl { margin:0; }
.sb-cols summary { font-size:12px; color:var(--accent); cursor:pointer; min-height:30px; display:flex; align-items:center; }
.sb-cols > div { display:flex; flex-wrap:wrap; gap:0 6px; padding:4px 0; }
.sb-bk-wrap { overflow-x:auto; max-width:100%; margin-top:6px; }
.sb-bk { width:100%; border-collapse:collapse; font-size:12px; font-variant-numeric:tabular-nums; }
.sb-bk th { font-size:10px; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); font-weight:600; padding:5px 4px; border-bottom:1px solid var(--border); text-align:left; vertical-align:bottom; }
.sb-bk th.num { text-align:right; }
.sb-bk td { padding:4px; border-bottom:1px solid var(--border-soft); vertical-align:top; }
.sb-bk td.sb-when { white-space:nowrap; }
.sb-guest { max-width:150px; overflow-wrap:anywhere; }
.sb-cell b { display:block; font-weight:600; }
.sb-cell span { display:block; font-size:11px; opacity:.8; }
.sb-unm { margin-top:10px; }
.sb-unm ul { list-style:none; margin:0; padding:0; font-size:12.5px; }
.sb-unm li { display:flex; gap:8px; padding:4px 0; border-bottom:1px solid var(--border-soft); flex-wrap:wrap; }
.sb-unm li > span:first-child { flex:1; min-width:0; }
.sb-empty { padding:16px 4px; font-size:12.5px; color:var(--text-muted); }
.sb-foot { display:flex; gap:10px; align-items:center; flex-wrap:wrap; padding:12px 20px; border-top:1px solid var(--border); background:var(--bg-subtle); border-radius:0 0 14px 14px; position:sticky; bottom:0; }
.sb-foot-l { font-size:12.5px; color:var(--text-muted); display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
.sb-err { flex:1; min-width:0; font-size:12.5px; color:var(--danger-text); }
.sb-err:empty { visibility:hidden; }
.sb-foot-r { display:flex; gap:8px; flex-wrap:wrap; margin-left:auto; }
@media (max-width: 900px) {
  .sb-overlay { padding:10px 8px; }
  .sb-tabs { display:flex; }
  .sb-body { grid-template-columns:minmax(0, 1fr); }
  .sb-left { border-right:none; }
  .sb-overlay[data-tab="ladder"] .sb-right { display:none; }
  .sb-overlay[data-tab="preview"] .sb-left { display:none; }
  .sb-bk thead { display:none; }
  .sb-bk, .sb-bk tbody { display:block; }
  .sb-bk tr { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:4px 10px; padding:8px; margin-bottom:8px; border:1px solid var(--border); border-radius:8px; }
  .sb-bk td { display:block; border:none; padding:2px 4px; text-align:left; border-radius:6px; }
  .sb-bk td::before { content:attr(data-label); display:block; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); }
  .sb-bk td.num { text-align:left; }
  .sb-guest { max-width:none; }
  .sb-bk tr.sb-bwhy-tr { display:block; padding:0; margin:-6px 0 10px; border:none; }
  .sb-bk tr.sb-bwhy-tr td { display:block; padding:0; border:none; }
  .sb-bk tr.sb-bwhy-tr td::before { content:none; }
}
@media (max-width: 640px) {
  .sb-overlay { padding:0; }
  .sb-panel { border-radius:0; min-height:100%; }
  .sb-hdr { padding:12px 14px; }
  .sb-banner { margin:8px 14px 0; }
  .sb-left, .sb-right { padding:10px 14px 14px; }
  .sb-computed { margin:0 -14px; padding:8px 14px; }
  .sb-row { grid-template-columns:minmax(0, 1fr); gap:6px; }
  .sb-foot { padding:10px 14px; border-radius:0; }
  .sb-foot-r { width:100%; }
  .sb-foot-r .btn { flex:1; }
  /* the totals card: each ladder row = its label, then Formula · Statement · Δ underneath */
  .sb-tot thead { display:none; }
  .sb-tot, .sb-tot tbody { display:block; }
  .sb-tot tr { display:grid; grid-template-columns:repeat(3, minmax(0, 1fr)); gap:0 6px; padding:5px 0; border-bottom:1px solid var(--border-soft); }
  .sb-tot tbody th { grid-column:1 / -1; border:none; padding:0 2px 2px; }
  .sb-tot td, .sb-tot td.num { border:none; padding:2px 4px; text-align:left; }
  .sb-tot td::before { content:attr(data-label); display:block; font-size:9.5px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); }
  /* R121: a Δ with its causes takes the whole width under Formula · Statement (chips + Fix stay legible) */
  .sb-tot td.sb-d:has(.sb-whys) { grid-column:1 / -1; }
  .sb-tot .sb-whys { justify-content:flex-start; }
}
`;
